/**
 * ChatPanel — AI Assistant panel with session management
 *
 * Replaces the placeholder in features/workspace/ui/ChatPanel.tsx.
 * Supports multiple sessions (tabs), streaming, tool calls, and permissions.
 */
import i18n from '@papan/i18n'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, Send, MessageSquare, MessageCircle, Plus, X, StopCircle, History, AlertTriangle, BookKey, Copy } from 'lucide-react'
import { MessageList } from './MessageList.js'
import { UploadsPausedNotice } from './UploadsPausedNotice'
import { truncateAndResend, mergeDraft } from './editResend.js'
import { createSendGate } from './send-gate.js'
import { sendMidRun } from './send-mid-run.js'
import { createDraftMaterializer } from './draft-materialize.js'
import { setKeyedText, carryDraftText, carryDraftList, withoutSent, restoreListByKey, updateListItemById, setKeyedList } from './composer-state.js'
import { DEFAULT_PERSONA_ID } from '../core/persona.js'
import { ProjectWelcome, type ScopeLink } from './ProjectWelcome.js'
import { ContextIndicator } from './ContextIndicator.js'
import { DisplayOptionsMenu } from './DisplayOptionsMenu.js'
import { ExportMenu, formatTranscript } from './ExportMenu.js'
import { TodoPanel } from './TodoPanel.js'
import { FileAutocomplete } from './FileAutocomplete.js'
import { SlashCommandMenu, type SlashCommand } from './SlashCommandMenu.js'
import { CommandPalette } from './CommandPalette.js'
import { StashMenu } from './StashMenu.js'
import { usePromptStash, type StashEntry } from './usePromptStash.js'
import { TimelineDialog } from './TimelineDialog.js'
import { SessionListDialog } from './SessionListDialog.js'
import { AttachmentArea, processFiles, processDriveItems } from './AttachmentArea.js'
import { markAttachmentMoved } from './attachmentLogic.js'
import { useComposerAttachmentControls, unsendableAttachmentNotice, editNotSentMessage } from './composerAttachments.js'
import { ChatQuotaBanner } from './ChatQuotaBanner.js'
import { ComposerAttachButton } from './ComposerAttachButton.js'
import { AutoModeButton } from './AutoModeButton.js'
import { useConversationAutoMode } from './useConversationAutoMode.js'
import { SessionKeybookPopover } from './SessionKeybookPopover.js'
import { AddSessionCredentialDialog } from './AddSessionCredentialDialog.js'
import { CredentialPrompt } from './CredentialPrompt.js'
import { useSessionCredentials } from './useSessionCredentials.js'
import { sessionDialogEnvMapping, sessionDialogFields, hasOpenablePending } from './session-credential-dialog.js'
import { EditorContextChip } from './SelectionChip.js'
import { EditMessageButtons } from './EditMessageButtons.js'
import { ModelSelector, DEFAULT_MODEL } from './ModelSelector.js'
import { useReasoningVariant } from './useReasoningVariant.js'
import { DevToolRenderMenu, type DevMockScenario } from './DevToolRenderMenu.js'
import { DevTypographyMenu, loadDevChatType } from './DevTypographyMenu.js'
import { formatChatCost } from './money.js'
import { RestoreVersionsModal, describeRestored, type RestoredFile } from './RestoreVersionsModal.js'
import { useAgentSession, useAgentSessions, useAgentUsage, useSessionActivity, getClientId, describeSummarize, describeSteerRefusal, isTurnLive } from './hooks.js'
import { presenceHeartbeat, openTabPresenceIds } from './presence-heartbeat.js'
import type { Attachment } from './hooks.js'
import { request } from '@papan/api-client'
import { useToast, useTheme, getThemeClass, useTabOverview, useTabReorder, useWriteStatus, useBillingCurrency, useKeybookAvailable } from '@papan/core-ui/ui'
import { useDisplayOptions } from './useDisplayOptions.js'
import { useOpenTabs } from './useOpenTabs.js'
import { usePromptHistory } from './usePromptHistory.js'
import { useFrecency } from './useFrecency.js'
import { useWorkspaceFiles } from './useWorkspaceFiles.js'
import { isPapanDrag, readPapanDrag } from '@papan/core-ui/ui/papanDrag'
import type { MessageAction } from './MessageActionsMenu.js'

/** Format cost in USD — shows appropriate precision based on magnitude */
/**
 * A chat money figure, in the deployment's BILLING currency.
 *
 * ── WHY THIS NO LONGER PREFERS `displayMinor` ───────────────────────────
 *
 * It used to render the server's display-currency conversion whenever one was
 * present, falling back to a USD-formatted number otherwise. Both halves broke
 * when the charged figure became billing-native:
 *
 *  - `displayMinor` is derived from the USD `cost` column, so preferring it
 *    showed a conversion of a DIFFERENT number than the one every cap, invoice
 *    and dashboard uses — and preferring it is what made that the default;
 *  - the fallback hardcoded `$`, so a Malaysian tenant's ringgit rendered with
 *    a dollar sign.
 *
 * The server now always sends `displayMinor: null` (the display layer is
 * parked, plan section 7), and the amount is already in the billing currency,
 * so this formats it with that currency's own symbol and does no arithmetic.
 *
 * The `≈` is gone with the conversion: nothing is being approximated any more.
 *
 * The implementation lives in `./money.ts` — it moved there so a second chat
 * surface (the conflict chat, removed with revert in #768) could share it
 * without an import cycle, after that surface grew its own hardcoded `$`.
 */

/**
 * Dialog shown when editing a message that has file changes — offers restore
 * or keep. ("Revert AI changes & send" reversed the AI's edits from the inline
 * file text stored in patch parts; that text is no longer stored (#768), and
 * the version store is the one restoration source.)
 */
function EditSendDialog({ onChoice, onCancel }: {
  onChoice: (choice: 'restore' | 'keep') => void
  onCancel: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold themed-text mb-2">File changes detected</h3>
              <p className="themed-text-secondary text-sm">
                The AI made file changes. How would you like to handle them before resending?
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={() => onChoice('restore')}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <History size={14} />
            Restore versions & send
          </button>
          <button
            onClick={() => onChoice('keep')}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg themed-btn-secondary transition-colors"
          >
            Keep changes & send
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface ActiveTabInfo {
  fileId: string
  fileName: string
  filePath: string
  fileType: string
}

interface Props {
  workspaceId: string
  workspaceFolderId?: string | null
  onOpenFile?: (path: string, at?: { line?: number }) => void
  /** Settings → Storage, for the chat-storage banner's action (#758).
   *  Absent on surfaces with no router (the mobile sheet's host supplies
   *  it; a preview does not), which simply hides the button. */
  onManageChatStorage?: () => void
  /**
   * Open Settings → Billing. Fires from the error banner when a run was
   * refused for lack of AI credit AND the reader can actually fix it.
   *
   * A callback rather than a route because this package has no router and no
   * knowledge of the tenant prefix. Optional, and the button is hidden without
   * it — the same contract `onOpenFile` and `BillingPage`'s `onOpenBudgets`
   * use: a host that cannot offer the destination shows no affordance for it.
   */
  onOpenBilling?: () => void
  activeTab?: ActiveTabInfo | null
  editorSelectionRef?: React.RefObject<import('@papan/collab/client').EditorSelection | null>
  contextMutedRef?: React.RefObject<boolean>
  onMuteContext?: () => void
  /** Org email for scoping tabs in shared org workspaces (omit for personal tenant) */
  orgEmail?: string
  /** Mobile layout: hide the in-panel "AI Assistant" title (the overlay owns
   *  it), move display-options down to the session-tabs row, and portal the
   *  export + budget into `mobileHeaderSlot` (the overlay header). */
  mobile?: boolean
  /** Portal target (in the mobile overlay header) for export + budget. */
  mobileHeaderSlot?: HTMLElement | null
  /** Linked folders (display-ready, resolved by the host) for the welcome
   *  screen's visibility card. undefined = host didn't wire links — the
   *  welcome shows only the project-files row instead of claiming "none". */
  scopeLinks?: ScopeLink[]
  /** Container kind — the welcome card names the files row after the
   *  explorer's section ("In this Project" / "In this PDApp"). */
  entityKind?: 'project' | 'miniapp'
  /** Whether the caller can write to this workspace — gates paste/drop/
   *  local-attach uploads to the session-private store (#639, Task 3.4;
   *  the server enforces this independently via `mayWriteWorkspace`, so
   *  this is disclosure, not the security boundary). Undefined = host
   *  didn't wire a signal; defaults to `true` to preserve prior behavior
   *  for such a caller rather than silently blocking every attachment. */
  canWrite?: boolean
}

// Slash-command menu + command palette are hidden for now — the current set
// (/compact /new /sessions /timeline /export) isn't worth the surface. Flip to
// true to bring both back (all wiring is kept intact behind this flag).
const COMMAND_SURFACE_ENABLED = false

// Prompt stash (park/restore unsent drafts) is hidden from the UI for now — we
// don't want to maintain the surface. The wiring (usePromptStash + StashMenu)
// is kept intact; flip to true to bring the Archive button back.
const PROMPT_STASH_ENABLED = false
/** The composer key with NO chat tab open — the placeholder chat. ONE spelling: the composer writes under it
 *  (`activeSessionId`) and every way the placeholder becomes a chat carries from it (`materializeDraft`). */
const VIRTUAL_ID = '__new__'

export function ChatPanel({ workspaceId, workspaceFolderId, onOpenFile, onOpenBilling, activeTab, editorSelectionRef, contextMutedRef, onMuteContext, orgEmail, mobile = false, mobileHeaderSlot = null, scopeLinks, entityKind, canWrite: canWriteProp, onManageChatStorage }: Props) {
  // The attachment gates keep their historical default (`true` when the host didn't wire it); the
  // welcome card's access label takes the RAW prop, so an unwired host under-claims (#1406 I2).
  const canWrite = canWriteProp ?? true
  // Subscribe to editor context changes via custom event (avoids re-rendering editors)
  const [editorSelection, setEditorSelection] = useState<import('@papan/collab/client').EditorSelection | null>(null)
  const [contextMuted, setContextMuted] = useState(false)
  /** The member's own chat-storage figure, for the banner above the
   *  composer. Fetched once per mount and after a send, which is the only
   *  thing in this panel that moves it. A failure leaves it null and the
   *  banner silent — a warning is not worth an error of its own. */
  const [chatQuota, setChatQuota] = useState<{ totalBytes: number; limitBytes: number | null } | null>(null)
  useEffect(() => {
    const sync = () => {
      setEditorSelection(editorSelectionRef?.current ?? null)
      setContextMuted(contextMutedRef?.current ?? false)
    }
    window.addEventListener('papan:selection-update', sync)
    return () => window.removeEventListener('papan:selection-update', sync)
  }, [editorSelectionRef, contextMutedRef])

  const { sessions, loading, loaded, createSession, renameSession, setSessionAutoMode, removeAlwaysGrant, fetchSession, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, refresh: refreshSessions } = useAgentSessions(workspaceId, orgEmail)
  const {
    workspaceCost, costVisibility, refresh: refreshUsage,
  } = useAgentUsage(workspaceId)
  const deploymentCurrency = useBillingCurrency()
  // ── THE AUDIENCE DECIDES THE CURRENCY, NOT THE DEPLOYMENT ──────────
  //
  // The `user` totals are billing-native and belong in the deployment's
  // currency. The `operator` totals are PROVIDER cost, which providers invoice
  // in USD and which nothing converts — labelling those with the deployment
  // currency renamed dollars to ringgit on the one screen built to show the
  // platform what it pays.
  const totalsCurrency = costVisibility === 'operator' ? 'USD' : deploymentCurrency
  /**
   * Whether the per-message cost badge renders (0174).
   *
   * ONLY in the 'user' view. `null` is "not answered yet" and withholds — see
   * `useAgentUsage`. 'operator' withholds too, and that is a decision rather
   * than an omission: `agent_usage` carries no `parent_message_id`, so a
   * per-message PROVIDER figure could not roll up the subagent spend the
   * charged badge does include, and it would sit beside a session total that
   * does. A number that is quietly missing a component is worse than no number.
   *
   * The two TOTALS need no equivalent guard: the server returns 0 for both when
   * the mode is 'hidden', so the `> 0` conditions below already withhold them.
   */
  const showMessageCost = costVisibility === 'user'
  /**
   * Tooltips for the two totals.
   *
   * In the operator view these are the PLATFORM's provider cost, not the
   * customer's — the same digits mean a different thing, and an unlabelled
   * figure is exactly the plausible-wrong-number problem. Not i18n'd because
   * neither of these titles ever was; the operator view is platform-admin-only
   * and English is what the console speaks.
   */
  const workspaceCostTitle = costVisibility === 'operator'
    ? 'Total workspace AI cost — what the platform pays the provider'
    : 'Total workspace AI cost'
  const sessionCostTitle = costVisibility === 'operator'
    ? 'Session cost — what the platform pays the provider'
    : 'Session cost'
  const { options: displayOptions, toggleOption } = useDisplayOptions()
  const openTabs = useOpenTabs(workspaceId, orgEmail)
  // Per-tab running / finished-while-away state: the active tab has live
  // `status`, but backgrounded runs need the tenant-wide busy scan (shared,
  // 5s poll) so their tab keeps the running wave and shows a done dot when a
  // turn finishes off-tab.
  const { busyIds, doneIds, clearDone } = useSessionActivity(openTabs.activeId)
  // #841: an open chat tab whose session is busy may hold a card the store never saw
  // (a reload restored another chat; attach doesn't replay it) — keep it present.
  const presenceTabsRef = useRef<string[]>([])
  useEffect(() => {
    presenceTabsRef.current = openTabPresenceIds(openTabs.tabIds, busyIds)
  }, [openTabs.tabIds, busyIds])
  useEffect(() => presenceHeartbeat.register(() => presenceTabsRef.current), [])
  const [messageMap, setMessageMap] = useState<Record<string, string>>({})
  const activeSessionId = openTabs.activeId || VIRTUAL_ID
  const message = messageMap[activeSessionId] || ''
  /** The LIVE session id: materializing a draft moves it to the new session
   *  before React re-renders, so the composer writers key on this, never on the
   *  render's `activeSessionId` (which would still name the dead draft). */
  const activeSessionIdRef = useRef(activeSessionId)
  activeSessionIdRef.current = activeSessionId
  const setMessage = (text: string | ((prev: string) => string)) => {
    const key = activeSessionIdRef.current
    setMessageMap(prev => setKeyedText(prev, key, text))
  }
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)
  // Mobile session list — the "all recent chats" overlay (editor-tab-list
  // pattern: frosted fog + full-width rows, opened by tapping the centered
  // pill or the ⧉ count). Desktop keeps the ws-tabbar overview above.
  const [sessionListOpen, setSessionListOpen] = useState(false)
  const mobileHeaderRef = useRef<HTMLDivElement>(null)
  const [mobileHeaderH, setMobileHeaderH] = useState(0)
  const openSessionList = () => {
    // Anchor the sheet below the header (measured, since row-1 is gone on
    // mobile and the height varies with the active pill).
    setMobileHeaderH(mobileHeaderRef.current?.offsetHeight ?? 0)
    setSessionListOpen(true)
  }
  const history = usePromptHistory(workspaceId)
  const frecency = useFrecency(workspaceId)
  const wsFiles = useWorkspaceFiles(workspaceId)
  const [autocomplete, setAutocomplete] = useState({ visible: false, query: '', triggerPos: -1, selectedIndex: 0 })
  const [slash, setSlash] = useState({ visible: false, query: '', selectedIndex: 0 })
  /** The command registry, readable by the input handler — which is declared
   *  ABOVE the `slashCommands` memo, so it cannot close over it directly.
   *  Only `acceptsArgs` is read from here. */
  const slashCommandsRef = useRef<SlashCommand[]>([])
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [timelineOpen, setTimelineOpen] = useState(false)
  const stash = usePromptStash(workspaceId)
  // Follow-up delivery is steer-only for now: a message sent mid-run injects
  // into the running turn at the next step boundary (@sync OpenCode
  // SessionInput delivery=steer). The queue mode was removed.
  const [attachmentsMap, setAttachmentsMap] = useState<Record<string, Attachment[]>>({})
  // ── THE SESSION'S WALLET (0180) ───────────────────────────────────────
  //
  // Computed server-side and read here, never re-derived: deciding this needs
  // the session's wallet AND the viewer's, and the second is a membership
  // lookup the browser has no business doing.
  //
  // `=== false` on purpose. The field is ABSENT on an older API during a
  // rolling restart, and treating absent as locked would disable chat for
  // everyone the moment a deploy lagged — the failure direction that takes the
  // product down rather than the one that merely lets a 409 through.
  const activeSessionWallet = sessions.find(s => s.id === activeSessionId)?.wallet
  const walletLocked = activeSessionWallet?.canSend === false

  const attachments = attachmentsMap[activeSessionId] || []
  const setAttachments = useCallback((val: Attachment[] | ((prev: Attachment[]) => Attachment[])) => {
    setAttachmentsMap(prev => {
      const key = activeSessionIdRef.current
      const current = prev[key] || []
      const next = typeof val === 'function' ? val(current) : val
      return next.length ? { ...prev, [key]: next } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key))
    })
  }, [])
  const [isDragOver, setIsDragOver] = useState(false)
  const [showSessionKeybook, setShowSessionKeybook] = useState(false)
  const [showAddCredDialog, setShowAddCredDialog] = useState(false)
  /** Buffer of system messages to inject when draft session materializes */
  /** Single-flight for handleSend until the composer clears (send-gate.ts). */
  const sendGateRef = useRef(createSendGate())
  /** One in-flight create per draft (draft-materialize.ts). */
  const draftMaterializerRef = useRef(createDraftMaterializer())
  /** The session last created with NO tab open, reused until the active tab
   *  changes — a call from the render before that change must not make another. */
  const lastCreatedRef = useRef<string | null>(null)
  useEffect(() => { lastCreatedRef.current = null }, [openTabs.activeId])
  /** Bumped when a send clears the composer. A handleSend from a render older
   *  than the last clear holds the SENT text in its closure (React has not
   *  applied the clear yet) and must not send it again. */
  const sendClearSeqRef = useRef(0)
  const renderClearSeq = sendClearSeqRef.current
  const [pendingCredToFill, setPendingCredToFill] = useState<{ file?: string; label?: string; envMapping: Record<string, string>; requestId: string; isEdit?: boolean; currentValues?: Record<string, string>; currentVersion?: number; entryId?: string; provider?: string } | null>(null)
  const keybookBtnRef = useRef<HTMLButtonElement>(null)
  // Per-session editing state (in-memory only, lost on refresh)
  const [editingMap, setEditingMap] = useState<Record<string, string | null>>({})
  const editingMessageId = editingMap[activeSessionId] || null
  const setEditingMessageId = (id: string | null) => setEditingMap(prev => ({ ...prev, [activeSessionId]: id }))
  const [ghostTextMap, setGhostTextMap] = useState<Record<string, string>>({})
  const ghostText = (openTabs.activeId && ghostTextMap[openTabs.activeId]) || ''
  const setGhostText = (text: string) => {
    const id = openTabs.activeId
    if (!id) return
    setGhostTextMap(prev => text ? { ...prev, [id]: text } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id)))
  }
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL)
  const { reasoningVariant, selectReasoningVariant } = useReasoningVariant(selectedModel)
  // Per-session persona map — no picker UI anymore (ProjectWelcome replaced
  // it); existing sessions keep their saved persona, new ones use the default.
  const [personaMap, setPersonaMap] = useState<Record<string, string>>({})
  const personaId = personaMap[activeSessionId] || DEFAULT_PERSONA_ID
  // The restore modal's message AND mode are ONE value, set together — so an
  // opening can never inherit an earlier opening's mode. As two states, "Restore
  // versions & send" once ran in a stale 'after' mode and RE-APPLIED the AI
  // changes it was meant to undo (review 2026-09-23).
  const [restoreTarget, setRestoreTarget] = useState<{ messageId: string; mode: 'before' | 'after' } | null>(null)
  const toast = useToast()
  // #1292: the per-message inline budget, upload cancel and send guard — shared
  // with HeroChat (composerAttachments.ts). One composer list per chat, keyed
  // like `setAttachments` (the live session id).
  const attachmentControls = useComposerAttachmentControls({
    lists: attachmentsMap,
    activeKey: () => activeSessionIdRef.current,
    workspaceId,
    toast,
  })
  const { reserveInline, pickFor, keptForSend, refuseUnsendable: refuseUnsendableAttachments } = attachmentControls
  // Manual billing's write freeze. Safe outside `WriteStatusProvider` — the
  // hook resolves to "not paused" rather than throwing, so a ChatPanel mounted
  // in a share view or a test shows nothing instead of crashing.
  const { uploadsPaused } = useWriteStatus()
  /**
   * Is her own chat storage full? (todo 10)
   *
   * From the figure this panel ALREADY fetches for the banner above the
   * composer — no new request, and it answers on the FIRST attempt rather
   * than after one refusal. The first version remembered a refusal
   * instead, because I had looked for an endpoint and a provider and
   * missed this; Season found the banner.
   *
   * ⚠️ HER OWN CEILING ONLY. The organisation's quota is not in this
   * figure and the chat has no source for it, so an org-full member still
   * learns at the refusal — deliberately, rather than reintroducing a
   * remembered-refusal mechanism whose clearing has to be wired at every
   * site that frees space (it was missed twice, and a stale one leaves her
   * in front of a dead button, which is worse than the refusal).
   *
   * `>=` and a limit of 0: at the ceiling there is no room for anything,
   * which is the same line `ChatQuotaBanner`'s `blocked` level draws.
   */
  const ownStorageFull = chatQuota != null
    && chatQuota.limitBytes != null
    && chatQuota.totalBytes >= chatQuota.limitBytes
  // 0179: hide the session-keybook affordance on a keybook-disabled
  // deployment — EXCEPT when this session already holds credentials, which
  // keep working (the carve-out for pre-existing bindings) and whose owner
  // must still be able to see and manage them.
  const keybookAvailable = useKeybookAvailable()

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const editInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Welcome starters: drop the prompt into the active tab's draft and focus
  // the composer with the caret at the end (rAF waits for the re-render).
  const startFromWelcome = (prompt: string) => {
    setMessage(prompt)
    requestAnimationFrame(() => {
      const ta = inputRef.current
      if (!ta) return
      ta.focus()
      ta.setSelectionRange(ta.value.length, ta.value.length)
    })
  }

  const hasUploading = attachments.some(a => a.status === 'uploading')

  // Auto-resize textarea when message changes programmatically (e.g. DnD path insert)
  useEffect(() => {
    const ta = inputRef.current
    if (ta) {
      ta.style.height = 'auto'
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`
    }
  }, [message])

  // Cross-feature prefill (Phase 2 preview-sandbox "AI risk check"): when
  // another editor wants to hand off a security/review question, it
  // dispatches `papan:chat-prefill` with `{ detail: { text } }`. We replace
  // the ACTIVE session's draft and focus the textarea so the user can
  // edit/send.
  //
  // Read activeSessionId from the existing ref (kept up-to-date every
  // render) rather than calling setMessage — setMessage is recreated on
  // every render, so a closure captured at mount points at the FIRST
  // active session, not the user's current one. activeSessionIdRef +
  // setMessageMap is the stable pair.
  //
  // Uses a window event instead of prop drilling through Workspace
  // because the chat panel and the editor live in sibling Workspace
  // columns.
  // Ref mirror for activeTab — the prefill handler below is registered
  // once at mount (empty deps) so it can't read activeTab from closure.
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string; originFileId?: string }>).detail
      const text = detail?.text
      if (typeof text !== 'string' || !text) return
      const sid = activeSessionIdRef.current
      // Sanity check: if the dispatcher stamped its origin file but
      // the active tab has changed in the meantime, log it so we can
      // see this rare race in support data. We DON'T gate the prefill
      // on a match — user's most recent intent (the current active
      // tab) wins, since dropping the prefill silently would be more
      // confusing than landing it on the new tab.
      const originFileId = detail?.originFileId
      if (originFileId && activeTabRef.current?.fileId && originFileId !== activeTabRef.current.fileId) {
        console.warn(
          `[chat-prefill] origin file ${originFileId} differs from active tab ` +
            `${activeTabRef.current.fileId} — landing prefill on the active tab anyway`,
        )
      }
      setMessageMap(prev => ({ ...prev, [sid]: text }))
      requestAnimationFrame(() => inputRef.current?.focus())
    }
    window.addEventListener('papan:chat-prefill', handler)
    return () => window.removeEventListener('papan:chat-prefill', handler)
  }, [])

  // Sync open tabs with loaded sessions (also clears stale tabs when list is empty).
  // Only against a list that actually LOADED: a failed load (an API instance
  // restarting) is not "no chats", and pruning against it wiped every tab's open
  // chats and this tab's own chat (manual zero-downtime run B4 review).
  useEffect(() => {
    if (loaded) {
      openTabs.syncWithSessions(sessions.map(s => s.id))
    }
  }, [sessions, loaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load workspace preferences (model + persona) from DB on mount
  useEffect(() => {
    request<{ modelId: string | null; settings: Record<string, unknown> | null }>(`/api/workspaces/${workspaceId}/preferences`)
      .then(data => {
        if (data.modelId) setSelectedModel(data.modelId)
      })
      .catch(() => {})
  }, [workspaceId])

  // Listen for cross-tab model preference changes (dispatched from SSE in hooks.ts)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { workspaceId: string; modelId: string }
      if (detail.workspaceId === workspaceId && detail.modelId) {
        setSelectedModel(detail.modelId)
      }
    }
    window.addEventListener('papan:workspace-preference', handler)
    return () => window.removeEventListener('papan:workspace-preference', handler)
  }, [workspaceId])

  // Save model preference to DB when user changes it
  const handleModelSelect = useCallback(async (modelId: string) => {
    setSelectedModel(modelId)
    try {
      await request(`/api/workspaces/${workspaceId}/preferences`, {
        method: 'PUT',
        body: JSON.stringify({ modelId }),
      })
    } catch (err) {
      console.warn('[ChatPanel] Failed to save model preference:', err)
      // Retry once after a short delay
      setTimeout(() => {
        request(`/api/workspaces/${workspaceId}/preferences`, {
          method: 'PUT',
          body: JSON.stringify({ modelId }),
        }).catch(() => {})
      }, 1000)
    }
  }, [workspaceId])

  // Build display tabs — show real sessions + draft tabs
  const DRAFT_PREFIX = 'draft_'

  // ── Auto mode (useConversationAutoMode.ts — shared with HeroChat) ──
  const autoModeNotYetChat = activeSessionId === VIRTUAL_ID || activeSessionId.startsWith(DRAFT_PREFIX)
  const autoMode = useConversationAutoMode({
    chatId: autoModeNotYetChat ? null : activeSessionId, draftKey: activeSessionId,
    sessions, setSessionAutoMode, removeAlwaysGrant, fetchSession, listLoaded: loaded, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, toast,
  })
  const displayTabs = openTabs.tabIds.length === 0 && sessions.length === 0 && !loading
    ? [{ id: VIRTUAL_ID, title: '', model: '' }]  // No sessions at all — show virtual placeholder
    : openTabs.tabIds.map(id => {
        if (id.startsWith(DRAFT_PREFIX)) return { id, title: '', model: '' }
        return sessions.find(s => s.id === id) ?? null
      }).filter((s): s is NonNullable<typeof s> => !!s)

  // Safari-pages tab overview for the open session tabs. Geometry matches
  // .ws-tabbar-sm (pill 26px + row-gap 12px = 38px pitch, 6px pad).
  const overviewActiveId = displayTabs.find(t => t.id === VIRTUAL_ID || openTabs.activeId === t.id)?.id ?? null
  const sessionOverview = useTabOverview({ activeId: overviewActiveId, measureKey: `${displayTabs.map(t => `${t.id}:${t.title}`).join('|')}::${overviewActiveId}`, stripHeight: 38, stripPad: 6 })
  // Rearranging the session tabs, the same gesture the editor's tab bar uses.
  // The hook hands back DISPLAY indices; `displayTabs` is `openTabs.tabIds`
  // filtered to the sessions that still exist, so the move is applied BY ID —
  // an index into one is not an index into the other. The virtual placeholder
  // has no stored tab at all, hence the guard.
  const sessionReorder = useTabReorder({
    stripRef: sessionOverview.stripRef,
    count: displayTabs.length,
    onReorder: (_from, _to, { movedId, targetId }) => {
      // BY ID, captured at drag start. `displayTabs` is filtered to sessions
      // that still exist, so resolving the indices here — LANDING_MS after
      // release — could name a different session, or none at all, silently
      // discarding the reorder the user just watched land.
      const moved = movedId || undefined
      const target = targetId || undefined
      if (!moved || !target || moved === VIRTUAL_ID || target === VIRTUAL_ID) return
      openTabs.reorderTabs(moved, target)
    },
  })

  const {
    messages,
    streamingText,
    streamingReasoning,
    activeToolCalls,
    status,
    pendingPermissions,
    pendingQuestions,
    expiredQuestionNotice,
    sessionCost,
    lastPersonaId,
    estimatedTokens,
    contextLimit,
    compacted,
    error,
    errorAction,
    sendMessage,
    steerMessage,
    skipOpenPrompts,
    cancel,
    dismissError,
    replyPermission,
    replyQuestion,
    rejectQuestion,
    restoreConversation,
    forkSession,
    summarize,
    summarizing,
    compacting,
    retry,
    reconnecting,
    turnActivity,
    todos,
    moveAttachmentPart,
    verifyMovedAttachmentByPath,
  } = useAgentSession(openTabs.activeId, { workspaceId, onComplete: refreshSessions, onOpenFile })

  // #662 slice 9 — the lazy clear seam for a SENT (transcript) chip: delegate
  // the open (and the host's own "File not found" toast) to `onOpenFile`
  // exactly as before, and additionally check whether a moved chip's drive
  // file still exists — clearing its stale "In the drive" tick if it was
  // deleted while this chat was closed. Only wired when the host gave an
  // opener, so a chip stays non-clickable when it has nothing to open.
  const handleTranscriptOpenFile = useCallback((path: string, at?: { line?: number }) => {
    onOpenFile?.(path, at)
    void verifyMovedAttachmentByPath(path)
  }, [onOpenFile, verifyMovedAttachmentByPath])

  // Session credentials — entryId-based binding model. With the
  // server-resolved per-turn pipeline the browser only tracks
  // metadata (file/label/envMapping/entryId/status). Actual values
  // are looked up by the API at job submit time. No auto-restore
  // useEffect: there's nothing to restore — the popover just shows
  // what session_credential rows say.
  const sessionCreds = useSessionCredentials({ onSyncRefused: (m) => toast.error(`Credential not added: ${m}`), onDropped: (m) => toast.warning(m, 10_000), activeToolCalls })

  /** The member's own chat-storage figure, for the banner above the
   *  composer (#758). Refetched whenever the transcript grows: a send is
   *  the only thing in this panel that moves it, and polling would be a
   *  request per interval for a banner most members never see. */
  useEffect(() => {
    let alive = true
    request<{ member: { totalBytes: number; limitBytes: number | null } | null }>('/api/agent/sessions/chat-storage')
      .then((r) => { if (alive) setChatQuota(r.member) })
      // Silent: the banner is a nudge. An error toast for a figure the
      // member never asked for would be worse than no banner.
      .catch(() => {})
    return () => { alive = false }
  }, [messages.length])

  // ─── DEV-ONLY tool-render preview ──────────────────────────────────
  // Drives hand-written mock agent tool UIs through the REAL render path
  // (MessageList prompt slots + an injected assistant message) so each
  // surface can be designed without a live agent run. Gated on
  // import.meta.env.DEV: the menu renders null and this stays null in
  // production, and every reply/dismiss below just clears the mock — no
  // network, no session/keybook mutation.
  const [devMock, setDevMock] = useState<DevMockScenario | null>(null)
  /** Dev-only chat typography experiment classes ('' in prod) */
  const [devChatType, setDevChatType] = useState<string>(loadDevChatType)
  const devMockQuestion = devMock?.kind === 'question' ? devMock.question : null
  const devMockPermission = devMock?.kind === 'permission' ? devMock.permission : null
  const devMockCred = devMock?.kind === 'question' ? devMock.sessionCred : undefined
  const displayMessages = devMock?.kind === 'toolcall' ? [...messages, devMock.message] : messages
  // Credential scenarios inject a matching pending session-cred row so the
  // keybook badge + popover row + session-restored form all show (the full
  // cred_to_env state). Merge into a DISPLAY-only list — the hook's real
  // state is untouched, so no server write and nothing to clean up.
  const displayCredentials = devMockCred ? [...sessionCreds.credentials, devMockCred] : sessionCreds.credentials

  // Load session credentials from server (persisted in SQLite per workspace)
  useEffect(() => {
    if (openTabs.activeId && workspaceId) {
      sessionCreds.load(openTabs.activeId, workspaceId)
    } else {
      sessionCreds.clear()
    }
  }, [openTabs.activeId, workspaceId])

  // Listen for keybook-entry deletes happening in this tab (e.g. from the
  // Keybook view). Page refreshes catch cross-tab / cross-device deletes
  // via the auto-restore 404 path above; this event covers the same-tab
  // case where the credential is still hot in React state.
  useEffect(() => {
    const onEntryDeleted = (e: Event) => {
      const entryId = (e as CustomEvent<{ entryId: string }>).detail?.entryId
      if (!entryId) return
      const matches = sessionCreds.credentials.filter(c => c.entryId === entryId)
      for (const m of matches) sessionCreds.remove(m.requestId, 'keybook-deleted')
    }
    window.addEventListener('papan:keybook-entry-deleted', onEntryDeleted)
    return () => window.removeEventListener('papan:keybook-entry-deleted', onEntryDeleted)
  }, [sessionCreds])


  const isProcessing = isTurnLive(status)

  /** The branch-point suggestion is OFFERABLE — drawn, and Tab-acceptable.
   *
   *  Three places had to agree on this and did not. The placeholder blanks
   *  itself for the ghost, but tests `isProcessing` FIRST — so mid-turn it
   *  rendered "Add instructions while it works…" while the overlay, testing
   *  only `ghostText && !message`, drew "Tab ⇥ <suggestion>" on top of it.
   *  Two strings in one box. The collision guard existed; the precedence
   *  defeated it. Reported after branching a session, which is exactly when a
   *  ghost is set.
   *
   *  Hidden during a turn rather than merely undrawn, because Tab would
   *  otherwise accept a suggestion nobody can see — and `handleSend` routes to
   *  `steerMessage` while processing, so accepting it would STEER the running
   *  turn with text written to start a fresh one. The ghost lives in
   *  `ghostTextMap` and comes back when the composer is free. */
  //
  //  THE COMPOSER SAYS ONE THING AT A TIME.
  //
  //  `composerHint` is the whole precedence chain in one place, and `null`
  //  means "nothing to say" — which is the ONLY state where the ghost may
  //  draw. Deriving both from it makes the overlap structurally impossible
  //  rather than guarded condition-by-condition, which is how this shipped
  //  broken twice: the placeholder tested each of these BEFORE its own
  //  `ghost ? ''` branch, so any of them left the placeholder non-empty while
  //  the overlay drew anyway. Fixing `isProcessing` alone still left the
  //  reported case — "Editing message above…" with the branch suggestion
  //  through it. A hint added below is now suppressive for free.
  const composerHint: string | null =
    walletLocked ? 'Read-only — fork to continue'
    : isDragOver ? 'Drop files here...'
    : editingMessageId ? 'Editing message above...'
    : isProcessing ? 'Add instructions while it works…'
    : null

  const ghostVisible = !!ghostText && !message && composerHint === null

  // Restore persona from loaded messages (existing sessions)
  useEffect(() => {
    if (lastPersonaId && activeSessionId && !personaMap[activeSessionId]) {
      setPersonaMap(prev => ({ ...prev, [activeSessionId]: lastPersonaId }))
    }
  }, [lastPersonaId, activeSessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh workspace cost when session completes
  const prevStatusRef = useRef(status)
  useEffect(() => {
    if (prevStatusRef.current === 'processing' && status === 'idle') {
      refreshUsage()
    }
    prevStatusRef.current = status
  }, [status, refreshUsage])

  // ─── Attachment handlers ─────────────────────────────────────────
  const handleAddAttachment = useCallback((att: Attachment) => {
    setAttachments(prev => [...prev, att])
  }, [])

  // By chip id, in whichever chat holds it (`updateListItemById`): an upload
  // finishes after the user may have switched chats, and an update keyed on the
  // chat on screen missed the chip — it spun forever and (#1292) blocked that
  // chat's Send.
  const handleRemoveAttachment = useCallback((id: string) => {
    attachmentControls.cancel(id)
    setAttachmentsMap(prev => updateListItemById(prev, id, () => null))
  }, [attachmentControls.cancel])

  const handleUpdateProgress = useCallback((id: string, progress: number) => {
    setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, progress })))
  }, [])

  const handleAttachmentReady = useCallback((id: string, updates: Partial<Attachment>) => {
    attachmentControls.settled(id, false, updates.handle)
    setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, ...updates, status: 'ready' as const })))
  }, [attachmentControls.settled])

  const handleAttachmentError = useCallback((id: string) => {
    attachmentControls.settled(id, true)
    setAttachmentsMap(prev => updateListItemById(prev, id, a => ({ ...a, status: 'error' as const })))
  }, [attachmentControls.settled])

  // #662 Task 8 — a COMPOSER (pre-send) chip was just moved to the drive.
  // There is no persisted message part to re-point yet (that's what
  // `moveAttachmentPart` does for an already-sent chip), so the fix is
  // local: patch the matching composer attachment so the eventual send
  // carries fileId+path instead of the stale inline/handle bytes.
  const handleComposerAttachmentMoved = useCallback((id: string, drive: { fileId: string; path: string }) => {
    setAttachmentsMap(prev => updateListItemById(prev, id, a => markAttachmentMoved(a, drive)))
  }, [])

  // A file was refused by routeFile (view-only caller tried to create new
  // bytes) — never uploaded, never inlined. Surface the reason and stop;
  // no attachment chip is ever added for it.
  const handleAttachmentBlocked = useCallback((filename: string, reason: string) => {
    toast.error(`${filename}: ${reason}`)
  }, [toast])

  const attachCallbacks = {
    onAdd: handleAddAttachment,
    onUpdateProgress: handleUpdateProgress,
    onReady: handleAttachmentReady,
    onError: handleAttachmentError,
    onBlocked: handleAttachmentBlocked,
  }

  /** Turn the draft tab (or no tab) into a real session — ONCE per draft, even
   *  when a paste/drop races the first send (draft-materialize.ts). Both racers
   *  run the carry-over below, in either order, so every step is idempotent
   *  (composer-state.ts): the second finds the draft's entries already moved
   *  and the tab already swapped. `keepText` carries the composer text over (an
   *  upload); a send drops it (it is the message being sent). `title` (the
   *  placeholder's rename, #1402) rides the create itself; a session handed
   *  back already made — by a racing drop or `lastCreatedRef` — is titled here
   *  after the carry. (Accepted: a rename that joins a FIRST SEND's create
   *  still in flight is titled after it, so the send's auto-title may win —
   *  that takes a send and a rename at the same moment.)
   *  Known, not handled here: `createSession` returns null on any failure,
   *  including one AFTER the server created the session — a retry then makes a
   *  second (empty) session. */
  // `inPlace` (a draft TAB's rename, #1402 owner review I1): the new chat takes the draft's place in the strip and is
  // active only if the draft was — a rename is not a send, so it never moves the tab to the end or switches to it (a
  // blur-commit after clicking another tab must leave the user there, typing under that tab's key).
  const materializeDraft = useCallback(async (draftId: string | null, keepText: boolean, title?: string, opts?: { inPlace?: boolean }): Promise<string | null> => {
    let createdHere = false
    const sid = !draftId && lastCreatedRef.current
      ? lastCreatedRef.current
      : await draftMaterializerRef.current.materialize(draftId, async () => {
        const created = await createSession(title, selectedModel, undefined, autoMode.draftValue(draftId ?? VIRTUAL_ID))
        if (created && !draftId) lastCreatedRef.current = created
        if (created) createdHere = true
        return created
      })
    if (!sid) return null
    // The composer key the chips and text were under: the draft tab's id, or —
    // with NO tab open — the placeholder `VIRTUAL_ID` (`activeSessionId` above).
    // That one used to carry nothing: a drive item dropped there (and the text)
    // stayed under the placeholder after the send, and came back to be sent
    // again the next time no tab was open (#1292 adoption, review round 3).
    const carryFrom = draftId ?? VIRTUAL_ID
    // the draft's OWN switch only (never the default — see `draftChoice`): the create above already sent what its chip showed
    const draftAuto = autoMode.draftChoice(carryFrom)
    if (draftAuto !== undefined) {
      // the chat already existed (`lastCreatedRef`): the create above didn't carry it
      if (!createdHere) { try { await setSessionAutoMode(sid, draftAuto) } catch { toast.error(i18n.t('agent:autoMode.saveFailed')) } }
      autoMode.clearDraft(carryFrom)
    }
    if (draftId) {
      const draftPersona = personaMap[draftId] || DEFAULT_PERSONA_ID
      setPersonaMap(prev => {
        if (!(draftId in prev) && sid in prev) return prev // already carried
        const { [draftId]: _, ...rest } = prev
        return { ...rest, [sid]: prev[draftId] || draftPersona }
      })
    }
    setAttachmentsMap(prev => carryDraftList(prev, carryFrom, sid))
    // #1292: the inline budget reads the lists synchronously — a paste that
    // runs right after this (before React re-renders) must see the draft's
    // chips under the NEW key, and its still-pending reservations too.
    attachmentControls.carry(carryFrom, sid, lists => carryDraftList(lists, carryFrom, sid))
    setMessageMap(prev => carryDraftText(prev, carryFrom, sid, keepText))
    const inPlace = !!(opts?.inPlace && draftId)
    if (inPlace) {
      openTabs.replaceTab(draftId!, sid)
    } else {
      if (draftId) openTabs.removeTab(draftId)
      openTabs.addTab(sid)
      openTabs.setActiveId(sid)
    }
    // Move the LIVE id now: the composer writers (setMessage, setAttachments)
    // and the attachment callbacks key on this ref, so they reach the new
    // session before React re-renders. In place, only if the draft WAS the one being typed in.
    if (!inPlace || activeSessionIdRef.current === carryFrom) activeSessionIdRef.current = sid
    if (title && !createdHere) {
      try { await renameSession(sid, title) } catch { toast.error(i18n.t('agent:session.renameAfterCreateFailed')) }
    }
    return sid
  }, [openTabs, createSession, renameSession, setSessionAutoMode, selectedModel, personaMap, autoMode.draftValue, autoMode.draftChoice, autoMode.clearDraft, attachmentControls.carry, toast])

  // Helper: materialize draft if needed, then upload to the session-private
  // CAS store (#639, Task 3.4) — never the drive's `.temp/{sessionId}/`.
  const processToSessionStore = useCallback(async (files: File[], source: 'paste' | 'drop' | 'attach') => {
    let sid = openTabs.activeId

    // Materialize draft on first file upload (so the upload targets a real
    // session ID, not a draft one)
    if (!sid || sid.startsWith(DRAFT_PREFIX)) {
      sid = await materializeDraft(sid, true)
      if (!sid) return
    }

    // Every chip of this pick belongs to the chat it uploads into. The files are
    // added one by one, each after the previous upload, so the user may have
    // switched chats in between — keyed on the chat on screen, a later chip
    // landed in the OTHER chat carrying this session's handle (refused there).
    const uploadInto = sid
    // #1292 review round 6: the pick can outlive this chat's tab (or the panel) —
    // a stale pick's later files are neither added nor uploaded.
    const pick = pickFor(uploadInto)
    processFiles(files, source, {
      ...attachCallbacks,
      onAdd: (att: Attachment) => { if (pick.alive()) setAttachmentsMap(prev => setKeyedList(prev, uploadInto, list => [...list, att])) },
    }, {
      sessionId: sid, workspaceId, canWrite,
      reserveInline: (id, bytes) => reserveInline(id, bytes, uploadInto),
      uploadSignal: pick.signal,
    })
  }, [openTabs, materializeDraft, attachCallbacks, workspaceId, canWrite, reserveInline, pickFor])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files)
    if (files.length === 0) return
    e.preventDefault()
    processToSessionStore(files, 'paste')
  }, [processToSessionStore])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    // Papan Drive items
    if (isPapanDrag(e.dataTransfer)) {
      const transferData = readPapanDrag(e.dataTransfer)
      if (transferData) {
        processDriveItems(transferData, {
          ...attachCallbacks,
          onInsertPath: (path) => {
            const quoted = `"${path}"`
            // When editing a message, the main chat input is hidden and
            // its textarea is disabled — route the path into the edit
            // bubble's textarea via a custom event instead.
            if (editingMessageId) {
              window.dispatchEvent(new CustomEvent('papan:edit-insert-path', { detail: quoted }))
              return
            }
            setMessage(prev => prev ? `${prev}\n${quoted}` : quoted)
          },
        }, { canWrite, reserveInline })
      }
      return
    }

    // OS file drop
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      processToSessionStore(files, 'drop')
    }
  }, [attachCallbacks, processToSessionStore, editingMessageId, canWrite, reserveInline])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    // Use 'copy' for OS files, 'move' for Papan internal DnD (it carries
    // PAPAN_DRAG_MIME; its effectAllowed is 'copyMove' so apps can take it too).
    e.dataTransfer.dropEffect = isPapanDrag(e.dataTransfer) ? 'move' : 'copy'
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only reset if leaving the container (not entering a child)
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setIsDragOver(false)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      processToSessionStore(files, 'attach')
    }
    e.target.value = '' // Reset so same file can be selected again
  }, [attachCallbacks, processToSessionStore])

  const handleSend = useCallback(async () => {
    const text = message.trim()
    const readyAttachments = attachments.filter(a => a.status === 'ready')
    if ((!text && readyAttachments.length === 0) || hasUploading) return
    // A press handled before React applied the last send's clear: this closure
    // still holds the text that was just sent.
    if (sendClearSeqRef.current !== renderClearSeq) return

    // One Enter sends ONCE: held until the composer clears (see send-gate.ts),
    // released before the turn is awaited so a mid-turn Enter still steers.
    const release = sendGateRef.current.tryEnter()
    if (!release) return
    let endSend = () => {}
    try {
      // A SLASH COMMAND IS NEVER A MESSAGE.
      //
      // Enter used to run the command only while the suggestion menu was open.
      // Once it had closed — typing past it, dismissing it, pasting the line —
      // the same keystroke sent `/compact` to the model as ordinary text. That
      // bought a full turn, put a `/compact` bubble in the transcript and got a
      // literal-minded answer back, which read exactly like the command having
      // "worked" on only the last message.
      //
      // Matched here rather than in the keydown handler so every send path is
      // covered — the button, Enter with the menu shut, and a pasted line —
      // and only when the name is a REAL command, so ordinary text that happens
      // to begin with a slash still sends.
      const asCommand = /^\/(\w+)(?:\s+([\s\S]+))?$/.exec(text)
      if (asCommand && readyAttachments.length === 0) {
        const cmd = slashCommandsRef.current.find(c => c.name === asCommand[1])
        // A command that takes no arguments only claims the line when the line IS
        // the command. `/new draft of the Q3 report` otherwise opened a blank
        // session and threw the prompt away — the text is not sent, not kept in
        // the composer, and recoverable only by pressing ↑. Falling through sends
        // it as an ordinary message, which is what a line with prose in it is.
        const claimsLine = cmd ? (cmd.acceptsArgs || !asCommand[2]) : false
        if (cmd && claimsLine) {
          setMessage('')
          setSlash({ visible: false, query: '', selectedIndex: 0 })
          history.append(text); history.resetNavigation()
          void cmd.run(cmd.acceptsArgs ? asCommand[2]?.trim() || undefined : undefined)
          return
        }
      }

      // A run is active → steer: inject the message into the running turn at the
      // next step boundary (@sync OpenCode SessionInput delivery=steer).
      let historyAppended = false // set when a steer attempt already recorded it
      if (isProcessing) {
        const active = openTabs.activeId
        if (!active || active.startsWith(DRAFT_PREFIX) || !text) return
        setMessage('')
        sendClearSeqRef.current++
        release()
        history.append(text); history.resetNavigation(); historyAppended = true
        // #648: skip an open prompt, else steer — the one rule for every chat surface (send-mid-run.ts)
        const outcome = await sendMidRun({
          text,
          hasOpenCards: pendingPermissions.length > 0 || pendingQuestions.length > 0,
          skip: skipOpenPrompts,
          steer: steerMessage,
          describeRefusal: describeSteerRefusal,
          stillFinishingNotice: i18n.t('agent:steer.stillFinishing'),
        })
        if (outcome.kind === 'steered') return
        if (outcome.kind === 'restore') {
          // By key: the user may have switched chats while the request was out.
          setMessageMap(p => setKeyedText(p, active, t => t || text))
          if (outcome.notice) toast.info(outcome.notice)
          return
        }
        // 'send': no run to steer into — send it as an ordinary prompt right now
      }

      // #1292 (owner): a failed attachment never lets the rest of the message go
      // without it. Checked AFTER the steer branch above: steering sends only
      // the text and leaves the chips where they are.
      if (refuseUnsendableAttachments(attachments)) {
        // A steer that fell through (`not_running`) already cleared the composer:
        // give the text back, or it survives only in ↑ history.
        if (historyAppended) {
          const back = openTabs.activeId ?? activeSessionIdRef.current
          setMessageMap(p => setKeyedText(p, back, t => t || text))
        }
        return
      }

      // #1292 D7: from here until the send ends, removing one of these chips holds
      // its server delete (the send may be promoting the row) — composerAttachments.
      endSend = attachmentControls.beginSend(readyAttachments.map(a => a.id))

      let sid = openTabs.activeId

      // Materialize draft session into a real server session
      if (!sid || sid.startsWith(DRAFT_PREFIX)) {
        sid = await materializeDraft(sid, false)
        if (!sid) return
      }

      if (text && !historyAppended) {
        history.append(text)
        history.resetNavigation()
      }

      // A draft's credential rows (and the notes of keybook-added ones) are saved to the new session BEFORE the first
      // message (round 11 — one await, the hook's flush); a no-op for a session that was never a draft
      if (sid) await sessionCreds.flushDraft(sid, workspaceId)

      const sendTab = contextMuted ? undefined : activeTab ?? undefined
      const sendSel = contextMuted ? undefined : editorSelection ?? undefined
      // Clear the input optimistically — `sendMessage` awaits the entire
      // SSE turn stream, so clearing after it leaves the just-sent text in
      // the box for the whole turn. `text`/`readyAttachments` are already
      // captured into locals above, so the call below is unaffected.
      // The chat this send belongs to — cleared, and on a failure restored, HERE,
      // never in whichever chat is on screen (the user may have switched during
      // the awaits above — a draft's credential flush is a network call — or
      // during the possibly long turn below; review round 4: the live-key
      // `setMessage('')` wiped the OTHER chat's typed draft).
      const sendKey = sid ?? activeSessionIdRef.current
      // A mid-run send already cleared it before its skip / steer (#648: the skip can wait up to a
      // minute) — clearing again could wipe a draft typed since.
      if (!historyAppended) setMessageMap(p => setKeyedText(p, sendKey, ''))
      // Only what this send took: a chip pasted while the session was being
      // created is still uploading into this composer.
      setAttachmentsMap(p => setKeyedList(p, sendKey, list => withoutSent(list, readyAttachments)))
      sendClearSeqRef.current++
      release()
      // #1292 D7 (review round 2): a chip the user removed while this send was
      // getting ready is NOT sent — removing is "don't send this". Read now, after
      // the last await, outside any state updater (React may run one later, after
      // the finally has cleared the flag).
      const sending = keptForSend(readyAttachments) ?? []
      if (!text && sending.length === 0) return
      const success = await sendMessage(text, sending.length > 0 ? sending : undefined, selectedModel, sid, personaId, sendTab, sendSel, getClientId(), undefined, reasoningVariant)
      if (!success) {
        // Restore so a failed send doesn't lose the message — but only if
        // the user hasn't started a new draft for this session meanwhile.
        setMessageMap(p => setKeyedText(p, sendKey, t => t || text))
        // Asked again now, outside any updater and before the finally's endSend():
        // a chip removed during the turn must not come back with a deleted handle.
        const back = keptForSend(sending) ?? []
        setAttachmentsMap(p => restoreListByKey(p, sendKey, back))
      }
      // On failure the message is restored to the input; error also shown
      // via state.error.
    } finally {
      release()
      endSend()
    }
  }, [message, attachments, isProcessing, hasUploading, renderClearSeq, openTabs, materializeDraft, sendMessage, steerMessage, history, selectedModel, activeTab, editorSelection, contextMuted, workspaceId, personaId, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend, skipOpenPrompts, pendingPermissions.length, pendingQuestions.length])

  // Autocomplete: compute filtered + frecency-sorted items
  const autocompleteItems = useMemo(() => {
    if (!autocomplete.visible) return []
    const q = autocomplete.query.toLowerCase()
    if (!q) return frecency.sortByFrecency(wsFiles.files).slice(0, 8)

    // Score: higher is better
    const scored = wsFiles.files
      .map(f => {
        const lower = f.toLowerCase()
        const isFolder = f.endsWith('/')
        const clean = isFolder ? lower.slice(0, -1) : lower
        const lastSlash = clean.lastIndexOf('/')
        const name = lastSlash >= 0 ? clean.slice(lastSlash + 1) : clean

        let score = 0
        if (name === q) score = 100            // exact name match
        else if (name.startsWith(q)) score = 80 // name starts with query
        else if (name.includes(q)) score = 60   // name contains query
        else if (lower.includes(q)) score = 20  // path contains query
        else return null                         // no match

        return { path: f, score }
      })
      .filter((x): x is { path: string; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .map(x => x.path)

    return scored.slice(0, 12)
  }, [autocomplete.visible, autocomplete.query, wsFiles.files, frecency])

  // Handle input change — detect @ trigger for autocomplete
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setMessage(value)
    if (ghostText) setGhostText('')

    const cursorPos = e.target.selectionStart
    // Walk backwards from cursor to find @ at word boundary
    let triggerPos = -1
    for (let i = cursorPos - 1; i >= 0; i--) {
      const ch = value[i]
      if (ch === '@') {
        // Valid trigger: at start of input or preceded by whitespace
        if (i === 0 || /\s/.test(value[i - 1])) {
          triggerPos = i
        }
        break
      }
      // Stop if we hit whitespace before finding @
      if (/\s/.test(ch)) break
    }

    if (triggerPos >= 0) {
      const query = value.slice(triggerPos + 1, cursorPos)
      setAutocomplete({ visible: true, query, triggerPos, selectedIndex: 0 })
      wsFiles.ensureLoaded()
    } else {
      if (autocomplete.visible) {
        setAutocomplete(prev => ({ ...prev, visible: false }))
      }
    }

    // Slash-command trigger. A bare "/" + word chars matches any command; a
    // space dismisses, EXCEPT for a command that declares `acceptsArgs` — there
    // the rest of the line is the argument (`/compact the auth bug`), so the
    // menu has to survive the space that would otherwise close it.
    const slashMatch = COMMAND_SURFACE_ENABLED ? /^\/(\w*)(\s[\s\S]*)?$/.exec(value) : null
    const argPart = slashMatch?.[2]
    const namedCommandTakesArgs = !!argPart
      && slashCommandsRef.current.some(c => c.name === slashMatch![1] && c.acceptsArgs)
    if (slashMatch && (!argPart || namedCommandTakesArgs)) {
      setSlash({ visible: true, query: slashMatch[1], selectedIndex: 0 })
    } else if (slash.visible) {
      setSlash(prev => ({ ...prev, visible: false }))
    }
  }, [autocomplete.visible, wsFiles, slash.visible])

  // Handle file selection from autocomplete
  const handleFileSelect = useCallback((path: string) => {
    const { triggerPos } = autocomplete
    const textarea = inputRef.current
    if (triggerPos < 0 || !textarea) return

    // Replace @query with the selected path
    const before = message.slice(0, triggerPos)
    const cursorPos = textarea.selectionStart
    const after = message.slice(cursorPos)
    const quoted = `"${path}"`
    const newMessage = `${before}${quoted}${after}`
    setMessage(newMessage)
    setAutocomplete({ visible: false, query: '', triggerPos: -1, selectedIndex: 0 })
    frecency.recordAccess(path)

    // Restore focus and set cursor after inserted text
    const newCursorPos = triggerPos + quoted.length
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }, [autocomplete, message, frecency])

  // ─── Slash commands ──────────────────────────────────────────────────
  // Mirrors OpenCode's actual command set (/compact, /export, /new, /sessions,
  // /timeline, /model...) mapped to Papan actions — NOT invented commands.
  const slashCommands = useMemo<SlashCommand[]>(() => [
    {
      name: 'compact',
      description: 'Summarize the conversation — add a focus, e.g. /compact the auth bug',
      acceptsArgs: true,
      run: async (args?: string) => {
        const t = describeSummarize(await summarize(args))
        toast[t.kind](t.text)
      },
    },
    { name: 'new', description: 'Start a new session', run: () => {
      const draftId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
      openTabs.addTab(draftId)
      openTabs.setActiveId(draftId)
    } },
    { name: 'sessions', description: 'Browse and reopen sessions', run: () => setHistoryOpen(true) },
    { name: 'timeline', description: 'Jump to or branch from a past message', run: () => setTimelineOpen(true) },
    { name: 'export', description: 'Copy the transcript to the clipboard', run: async () => {
      try { await navigator.clipboard.writeText(formatTranscript(messages)); toast.success('Transcript copied') }
      catch { toast.error('Copy failed') }
    } },
  ], [summarize, toast, messages])
  slashCommandsRef.current = slashCommands

  const slashFiltered = useMemo(() => {
    const q = slash.query.toLowerCase()
    if (!q) return slashCommands
    return slashCommands.filter(c => c.name.includes(q)).sort((a, b) =>
      Number(b.name.startsWith(q)) - Number(a.name.startsWith(q)))
  }, [slashCommands, slash.query])

  const runSlashCommand = useCallback((cmd: SlashCommand) => {
    // Read the argument off the CURRENT input rather than `slash.query`: the
    // query is the command NAME (it is what the menu filters on), so taking
    // args from it would pass the name back as the argument.
    const typed = inputRef.current?.value ?? ''
    const args = cmd.acceptsArgs
      ? /^\/\w*\s+([\s\S]+)$/.exec(typed)?.[1]?.trim() || undefined
      : undefined
    setSlash({ visible: false, query: '', selectedIndex: 0 })
    setMessage('')
    cmd.run(args)
  }, [])

  // Command palette (Cmd/Ctrl+K) over the same command registry.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (COMMAND_SURFACE_ENABLED && (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setPaletteOpen(v => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Autocomplete keyboard handling (takes priority)
    if (autocomplete.visible) {
      if (e.key === 'Escape') {
        e.preventDefault()
        setAutocomplete(prev => ({ ...prev, visible: false }))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setAutocomplete(prev => ({ ...prev, selectedIndex: Math.max(0, prev.selectedIndex - 1) }))
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setAutocomplete(prev => ({ ...prev, selectedIndex: Math.min(autocompleteItems.length - 1, prev.selectedIndex + 1) }))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const item = autocompleteItems[autocomplete.selectedIndex]
        if (item) handleFileSelect(item)
        return
      }
    }

    // Slash-command keyboard handling (takes priority over send)
    if (slash.visible && slashFiltered.length > 0) {
      if (e.key === 'Escape') { e.preventDefault(); setSlash(prev => ({ ...prev, visible: false })); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSlash(prev => ({ ...prev, selectedIndex: Math.max(0, prev.selectedIndex - 1) })); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlash(prev => ({ ...prev, selectedIndex: Math.min(slashFiltered.length - 1, prev.selectedIndex + 1) })); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const cmd = slashFiltered[slash.selectedIndex]
        if (cmd) runSlashCommand(cmd)
        return
      }
    }

    // Tab: accept ghost text
    if (e.key === 'Tab' && ghostVisible) {
      e.preventDefault()
      setMessage(ghostText)
      setGhostText('')
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      // An IME commit is not a send; let the IME have the key.
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return
      e.preventDefault()
      // A held Enter auto-repeats (macOS: from 150 ms) — one press, one send.
      if (e.repeat) return
      handleSend()
      return
    }

    // Arrow-up: cycle backward through history
    if (e.key === 'ArrowUp' && !e.shiftKey && history.entries.length > 0) {
      const textarea = inputRef.current
      if (textarea && (textarea.selectionStart === 0 || message === '')) {
        e.preventDefault()
        const result = history.navigateUp(message)
        if (result !== null) setMessage(result)
      }
    }

    // Arrow-down: cycle forward through history
    if (e.key === 'ArrowDown' && !e.shiftKey && history.index >= 0) {
      e.preventDefault()
      const result = history.navigateDown()
      if (result !== null) setMessage(result)
    }
  }

  const handleNewSession = () => {
    // Client-only draft — server session created on first message send
    const draftId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
    openTabs.addTab(draftId)
    openTabs.setActiveId(draftId)
  }

  const handleCloseSession = (sessionId: string) => {
    if (openTabs.tabIds.length <= 1) return
    // #1292 D7: the closed tab's chips are dropped unsent — cancel their uploads and
    // delete what already landed (composerAttachments `discard`).
    attachmentControls.closeKey(sessionId)
    const newActive = openTabs.removeTab(sessionId)
    if (newActive !== undefined) openTabs.setActiveId(newActive)
    // Clean up per-session state
    setMessageMap(prev => { const { [sessionId]: _, ...rest } = prev; return rest })
    setGhostTextMap(prev => { const { [sessionId]: _, ...rest } = prev; return rest })
    setAttachmentsMap(prev => { const { [sessionId]: _, ...rest } = prev; return rest })
  }

  // What the rename box was filled with — a not-yet-real chat (the placeholder, a draft tab) committed UNCHANGED makes
  // nothing: a double-click then a click away must not create an empty chat called "Chat N" (#1438 owner review).
  const renamePrefillRef = useRef<string | null>(null)
  const startRename = (sessionId: string, currentTitle: string, index: number) => {
    setEditingSessionId(sessionId)
    renamePrefillRef.current = currentTitle || `Chat ${index + 1}`
    setEditingTitle(renamePrefillRef.current)
    setTimeout(() => editInputRef.current?.select(), 0)
  }

  // One commit per chat at a time: Enter, then a blur while it is still saving, must not commit that chat again — a
  // rename of ANOTHER chat meanwhile is its own. The ref answers synchronously; the state makes the input read-only
  // while it saves (an edit typed then would be dropped).
  const renamingRef = useRef(new Set<string>())
  const [renamingIds, setRenamingIds] = useState<ReadonlySet<string>>(new Set())
  // `via`: Enter is a deliberate commit — an unchanged pre-filled name then still makes the chat; only a blur (a click
  // away) with the name unchanged creates nothing (#1438 owner review).
  const commitRename = async (via: 'enter' | 'blur' = 'blur') => {
    if (!editingSessionId || renamingRef.current.has(editingSessionId)) return
    const trimmed = editingTitle.trim()
    if (!trimmed) { setEditingSessionId(null); return }
    const notYetAChat = editingSessionId === VIRTUAL_ID || editingSessionId.startsWith(DRAFT_PREFIX)
    if (via === 'blur' && notYetAChat && trimmed === renamePrefillRef.current?.trim()) { setEditingSessionId(null); return }

    // Renaming the no-tab placeholder makes it a real chat: through the SAME
    // create + carry as its first send or upload (`materializeDraft`), so the
    // chips and text under the placeholder key move to the new chat — a create
    // of its own left them behind, to reappear (and be sent again) the next
    // time no tab was open (#1402). The text is kept (`keepText`): nothing was
    // sent. The title rides the create (`materializeDraft` titles a session it
    // was handed already made).
    const targetId = editingSessionId
    renamingRef.current.add(targetId)
    setRenamingIds(new Set(renamingRef.current))
    try {
      if (targetId === VIRTUAL_ID) {
        if (!(await materializeDraft(null, true, trimmed))) toast.error(i18n.t('agent:session.renameCreateFailed'))
      } else if (targetId.startsWith(DRAFT_PREFIX)) {
        // A draft tab (a "+" chat before its first send) has no session yet — its rename used to PATCH the draft id and
        // fail every time (#1402 owner review, I1). It becomes a real chat through the same create + carry, IN PLACE.
        if (!(await materializeDraft(targetId, true, trimmed, { inPlace: true }))) toast.error(i18n.t('agent:session.renameCreateFailed'))
      } else {
        try { await renameSession(targetId, trimmed) } catch { toast.error(i18n.t('agent:session.renameFailed')) }
      }
    } finally {
      renamingRef.current.delete(targetId)
      setRenamingIds(new Set(renamingRef.current))
      // only if the editor is still on THIS chat (a rename of another may have been started meanwhile)
      setEditingSessionId(cur => (cur === targetId ? null : cur))
    }
  }

  const cancelRename = () => {
    setEditingSessionId(null)
  }

  const handleMessageAction = useCallback(async (action: MessageAction, messageId: string) => {
    switch (action) {
      case 'restore-versions': {
        setRestoreTarget({ messageId, mode: 'before' })
        break
      }
      case 'restore-versions-after': {
        setRestoreTarget({ messageId, mode: 'after' })
        break
      }
      case 'summarize': {
        const t = describeSummarize(await summarize())
        toast[t.kind](t.text)
        break
      }
      case 'branch': {
        // Pre-fill the branched-from message's text as ghost text (fallback if user exits edit mode)
        const branchMsg = messages.find(m => m.id === messageId)
        const branchText = branchMsg?.parts
          // Not a cleared-file note (0353) — see MessageBubble's edit text.
          .filter(p => p.type === 'text' && !p.cleared)
          .map(p => (p as { content: string }).content)
          .join('') ?? ''

        const result = await forkSession(messageId)
        if (result) {
          await refreshSessions()
          openTabs.addTab(result.id)
          openTabs.setActiveId(result.id)
          if (branchText) setGhostTextMap(prev => ({ ...prev, [result.id]: branchText }))
          // Auto-open edit mode on the branch-point message (it was copied with a new id)
          if (result.branchedMessageID) {
            setEditingMap(prev => ({ ...prev, [result.id]: result.branchedMessageID! }))
          }
        }
        break
      }
    }
  }, [summarize, forkSession, refreshSessions, openTabs, toast])

  /** Fork a read-only session into the caller's current wallet and open it. */
  const forkForWallet = useCallback(async () => {
    const result = await forkSession()
    if (!result) {
      toast.error('Could not fork this conversation')
      return
    }
    await refreshSessions()
    openTabs.addTab(result.id)
    openTabs.setActiveId(result.id)
  }, [forkSession, refreshSessions, openTabs, toast])

  // Pending edit — stored when user clicks Send while there are file changes
  const pendingEditRef = useRef<{ messageId: string; text: string } | null>(null)
  const [editSendDialog, setEditSendDialog] = useState<{ messageId: string; text: string } | null>(null)

  const handleEditSend = useCallback(async (messageId: string, editedText: string) => {
    // Pre-check: abort if session is busy (avoids deleting messages then failing to send)
    if (status === 'processing' || status === 'submitting') {
      toast.error('Session is busy — please wait or dismiss the error first')
      return
    }
    // Check if there are file changes from this message onwards (not session-wide)
    const msgIdx = messages.findIndex(m => m.id === messageId)
    const hasFileChanges = msgIdx >= 0 && messages.slice(msgIdx).some(m =>
      m.parts?.some((p: any) => p.type === 'patch' && (p as any).snapshots?.length > 0)
    )
    // #1292: checked before the dialog and before the truncation — a refused
    // resend must not have deleted the original message first.
    if (refuseUnsendableAttachments(attachments)) return
    if (hasFileChanges) {
      setEditSendDialog({ messageId, text: editedText })
      return
    }
    const editAttachments = attachments.length > 0 ? attachments : undefined
    // The chat being edited: the resend's turn can outlast a switch to another chat.
    const editKey = activeSessionIdRef.current
    // #639 Task 3.8: handles of attachments this edit is KEEPING (about to be
    // resent) — excluded from the restore's synchronous dereference, or the
    // resend below would reference a row the truncation just deleted.
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      // A chip removed during the truncation is not sent (read when the send starts).
      send: () => sendMessage(editedText, keptForSend(editAttachments), selectedModel, undefined, personaId, activeTab && !contextMuted ? activeTab : undefined, editorSelection && !contextMuted ? editorSelection : undefined, getClientId(), undefined, reasoningVariant),
      // Clear attachments — they belonged to this edit, not to the still-visible
      // main chat input (which shares the same state while editing is active).
      onSent: () => { if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments))); setEditingMessageId(null) },
      // R1.2: the truncation already removed the original, but the send didn't land
      // (session busy). Keep the edited text in the composer so it isn't lost; drop
      // edit mode. Attachments are retained for the retry.
      onFailed: () => {
        setMessage(prev => mergeDraft(prev, editedText))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [messages, restoreConversation, sendMessage, selectedModel, personaId, status, toast, attachments, activeTab, contextMuted, editorSelection, reasoningVariant, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend])

  const handleEditSendChoice = useCallback(async (choice: 'restore' | 'keep') => {
    const pending = editSendDialog
    if (!pending) return
    setEditSendDialog(null)

    // #1292: the chips may have changed while the dialog was open — checked
    // before EITHER choice, so a refused resend restores no files either.
    if (refuseUnsendableAttachments(attachments)) return

    if (choice === 'restore') {
      // Open restore modal — store pending edit for after restore completes
      pendingEditRef.current = pending
      // 'before': undo the AI's changes before resending. Set WITH the message
      // (one state), so it can never be a stale 'after' — see `restoreTarget`.
      setRestoreTarget({ messageId: pending.messageId, mode: 'before' })
      return
    }

    const editAttachments = attachments.length > 0 ? attachments : undefined
    // The chat being edited: the resend's turn can outlast a switch to another chat.
    const editKey = activeSessionIdRef.current
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(pending.messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      send: () => sendMessage(pending.text, keptForSend(editAttachments), selectedModel, undefined, personaId, activeTab && !contextMuted ? activeTab : undefined, editorSelection && !contextMuted ? editorSelection : undefined, getClientId(), undefined, reasoningVariant),
      onSent: () => { if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments))); setEditingMessageId(null) },
      onFailed: () => { // R1.2: retain the edited draft on a failed send after truncation
        setMessage(prev => mergeDraft(prev, pending.text))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [editSendDialog, restoreConversation, sendMessage, selectedModel, personaId, toast, attachments, activeTab, contextMuted, editorSelection, reasoningVariant, refuseUnsendableAttachments, attachmentControls.beginSend, keptForSend])

  // Called when restore modal completes from a pending edit
  const handleRestoreAndSendComplete = useCallback(async (restored: RestoredFile[]) => {
    const pending = pendingEditRef.current
    if (!pending) return
    pendingEditRef.current = null
    setRestoreTarget(null)
    // #1292: a chip failed while the restore ran. The files ARE restored; the
    // resend (and its truncation) must not run. Stay in edit mode — the edit's
    // text lives on in its bubble — so the user fixes the chip and sends the
    // edit again, rather than appending it after the untruncated original.
    // The modal calls back with the callback it captured at the Restore click, so
    // read the LIVE list (the ref), not this closure's.
    const liveAttachments = attachmentControls.liveList()
    const unsendable = unsendableAttachmentNotice(liveAttachments)
    if (unsendable) {
      toast.error(restored.length > 0 ? editNotSentMessage(describeRestored(restored), unsendable.message) : unsendable.message)
      return
    }
    const editAttachments = liveAttachments.length > 0 ? liveAttachments : undefined
    const editKey = activeSessionIdRef.current
    // Asked when the truncation STARTS (keptForSend): a chip removed before then is
    // neither kept nor sent. One removed DURING the truncation is kept but not sent —
    // a hydrated (already attached) one is then reaped by the attached-row sweep.
    const keepAttachmentHandles = () => keptForSend(editAttachments)?.map(a => a.handle).filter((h): h is string => !!h)
    const endSend = attachmentControls.beginSend((editAttachments ?? []).map(a => a.id))
    try { await truncateAndResend({
      restore: () => restoreConversation(pending.messageId, { inclusive: true, keepAttachmentHandles: keepAttachmentHandles() }),
      // Same send as the "keep" path — reasoning variant included, and the
      // edit's attachments cleared from the composer once sent (review
      // 2026-09-23: "Send without restoring" routes more resends through here).
      send: () => sendMessage(pending.text, keptForSend(editAttachments), selectedModel, undefined, personaId, activeTab && !contextMuted ? activeTab : undefined, editorSelection && !contextMuted ? editorSelection : undefined, getClientId(), undefined, reasoningVariant),
      onSent: () => {
        if (editAttachments) setAttachmentsMap(p => setKeyedList(p, editKey, list => withoutSent(list, editAttachments)))
        setEditingMessageId(null)
        if (restored.length > 0) {
          toast.success(`${describeRestored(restored)} and resent`)
        }
      },
      // R1.2: retain the edited draft on a failed send after truncation (the success
      // toast now only fires on success, so it can't claim a resend that didn't happen).
      onFailed: () => {
        setMessage(prev => mergeDraft(prev, pending.text))
        setEditingMessageId(null)
        toast.error('Session busy — your edit was kept in the composer. Press Send to retry.')
      },
    }) } finally { endSend() }
  }, [restoreConversation, sendMessage, selectedModel, personaId, toast, attachments, activeTab, contextMuted, editorSelection, reasoningVariant, attachmentControls.beginSend, keptForSend])

  return (
    <div
      className={`h-full w-full min-w-0 ws-card flex flex-col relative${displayOptions.showDevTools && devChatType ? ` ${devChatType}` : ''}`}
      // Lift above the sibling cards while the overview is open so its
      // fixed scrim (in this card's stacking context) covers them.
      style={sessionOverview.open ? { zIndex: 30 } : undefined}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Drop overlay */}
      {isDragOver && (
        <div className="absolute inset-0 z-40 bg-brand-500/10 border-2 border-dashed border-brand-500/40 rounded-lg pointer-events-none flex items-center justify-center">
          <span className="text-sm text-brand-400 font-medium">Drop files here</span>
        </div>
      )}
      {/* Header */}
      <div ref={mobileHeaderRef} className={`border-b themed-border ${mobile ? 'relative z-40' : ''}`}>
        {/* Row 1 — title + export + display + budget. On mobile the overlay
            owns the title and hosts export/budget (portaled below), and
            display-options drops to the tabs row, so this row is desktop-only. */}
        {!mobile && (
          <div className="flex items-center px-2 pt-2 gap-1">
            <Sparkles size={14} className="text-brand-400 ml-1" />
            <span className="text-xs themed-text-muted uppercase tracking-wide ws-sparkle">AI Assistant</span>
            <div className="flex-1" />
            <ExportMenu
              messages={messages}
              sessionTitle={sessions.find(s => s.id === openTabs.activeId)?.title}
              sessionId={openTabs.activeId || undefined}
              workspaceId={workspaceId}
            />
            <DisplayOptionsMenu options={displayOptions} onToggle={toggleOption} />
            {workspaceCost > 0 && (
              <span className="text-[10px] themed-text-muted mr-1" title={workspaceCostTitle}>
                {formatChatCost(workspaceCost, totalsCurrency)}
              </span>
            )}
          </div>
        )}
        {/* Mobile: export + budget live OUTSIDE this card, in the overlay
            header's right-hand slot (same line as the back arrow + title). */}
        {mobile && mobileHeaderSlot && createPortal(
          <>
            <ExportMenu
              messages={messages}
              sessionTitle={sessions.find(s => s.id === openTabs.activeId)?.title}
              sessionId={openTabs.activeId || undefined}
              workspaceId={workspaceId}
            />
            {workspaceCost > 0 && (
              <span className="text-[10px] themed-text-muted" title={workspaceCostTitle}>
                {formatChatCost(workspaceCost, totalsCurrency)}
              </span>
            )}
          </>,
          mobileHeaderSlot,
        )}

        {mobile ? (
          /* Mobile: a single CENTERED active-session pill + controls. Tapping
             the pill (or the ⧉ count) opens the full "recent chats" list
             overlay below — same approach as the editor tab list. */
          <div className="flex items-center gap-1 px-2 py-1.5">
            <div className="flex min-w-0 flex-1 items-center justify-center">
              <button
                onClick={() => (sessionListOpen ? setSessionListOpen(false) : openSessionList())}
                aria-haspopup="menu"
                aria-expanded={sessionListOpen}
                className={`ws-pill mobile-tab-pill flex w-full min-w-0 items-center gap-2 px-3 ${sessionListOpen ? 'ws-pill-active themed-text' : 'themed-text-secondary'}`}
              >
                <MessageSquare size={15} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate text-center">
                  {(() => {
                    const a = displayTabs.find(t => t.id === VIRTUAL_ID || openTabs.activeId === t.id)
                    return a ? (a.id.startsWith(DRAFT_PREFIX) ? 'New chat' : (a.title || 'Chat')) : 'New chat'
                  })()}
                </span>
              </button>
            </div>
            <DisplayOptionsMenu options={displayOptions} onToggle={toggleOption} />
            <button
              onClick={() => { handleNewSession(); setSessionListOpen(false) }}
              aria-label="New chat"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg themed-text-muted active:themed-bg-tertiary"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => { setHistoryOpen(true); setSessionListOpen(false) }}
              aria-label="Session history"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg themed-text-muted active:themed-bg-tertiary"
            >
              <History size={16} />
            </button>
          </div>
        ) : (
        /* Desktop: Safari-pages overview (shared useTabOverview). The
           placeholder owns the layout slot; the strip is absolute and
           expands downward over the messages, tabs keeping their x. */
        <div className="relative" style={{ height: 38 }}>
          {sessionOverview.open && <div className="ws-tab-scrim" onClick={() => sessionOverview.setOpen(false)} />}
          <div
            ref={sessionOverview.rowRef}
            className={`ws-tabrow ${sessionOverview.open ? 'ws-tabrow-open' : ''}`}
            style={{ height: sessionOverview.maskHeight }}
          >
            <div
              ref={sessionOverview.stripRef}
              className="ws-tabbar ws-tabbar-sm flex items-center"
              style={{ transform: sessionOverview.stripTransform }}
            >
              {displayTabs.map((tab, idx) => {
                const isDraft = tab.id.startsWith(DRAFT_PREFIX)
                const isActive = tab.id === VIRTUAL_ID || openTabs.activeId === tab.id
                // Active tab has the live status; background tabs read the
                // shared busy scan. Done dot only on inactive finished tabs.
                const running = (isActive && isProcessing) || busyIds.has(tab.id)
                const done = !running && !isActive && doneIds.has(tab.id)
                return (
                  <div
                    key={tab.id}
                    data-tab-id={tab.id}
                    style={sessionReorder.styleFor(idx)}
                    // Not while this tab is being RENAMED. The inline input
                    // stops `click` but not `pointerdown`, so dragging across
                    // the text to select it claimed the reorder gesture: the
                    // pill lifted, `user-select: none` killed the selection
                    // being made, and on release a drag past the entry
                    // threshold committed a reorder the user never asked for.
                    // Guarding the wrapper covers the input and the pill's own
                    // padding alike. (The workspace TabBar has no inline input,
                    // which is why it never showed this.)
                    onPointerDown={e => { if (editingSessionId === tab.id) return; sessionReorder.onPointerDown(e, idx) }}
                    onClick={() => { if (sessionReorder.consumeClick()) return; if (tab.id !== VIRTUAL_ID) { openTabs.setActiveId(tab.id); clearDone(tab.id) } if (sessionOverview.open) sessionOverview.setOpen(false) }}
                    className={`ws-pill ws-pill-sm flex items-center gap-1.5 px-2 cursor-grab active:cursor-grabbing transition-all group ${
                      sessionReorder.state?.from === idx ? 'ws-pill-grabbed' : ''
                    } ${
                      // Mobile: full-width pills → the wrapped grid becomes
                      // one row per session, so collapsed shows just the
                      // active session and ⧉ expands the full list (same as
                      // the editor tab list).
                      mobile ? 'w-full max-w-none' : ''
                    } ${
                      isActive
                        ? 'ws-pill-active themed-text'
                        : 'themed-text-muted hover:themed-text'
                    }`}
                  >
                    <span className="chat-snav-lead">
                      {running ? (
                        <span className="chat-run-wave" title="Agent running" aria-hidden>
                          <span /><span /><span />
                        </span>
                      ) : done ? (
                        <span className="chat-done-dot" title="Finished while you were away" />
                      ) : (
                        <MessageCircle size={12} />
                      )}
                    </span>
                    {editingSessionId === tab.id ? (
                      <input
                        ref={editInputRef}
                        value={editingTitle}
                        onChange={e => setEditingTitle(e.target.value)}
                        onBlur={() => commitRename('blur')}
                        readOnly={renamingIds.has(tab.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename('enter')
                          if (e.key === 'Escape') cancelRename()
                        }}
                        onClick={e => e.stopPropagation()}
                        onPointerDown={e => e.stopPropagation()}
                        className="text-xs bg-transparent themed-text outline-none border-b border-brand-500 w-[80px] min-w-0"
                      />
                    ) : (
                      <span
                        className={`text-xs truncate ${mobile ? 'max-w-none' : 'max-w-[80px]'}`}
                        onDoubleClick={e => { e.stopPropagation(); startRename(tab.id, tab.title || '', idx) }}
                        title="Double-click to rename"
                      >
                        {isDraft ? 'New chat' : (tab.title || `Chat ${idx + 1}`)}
                      </span>
                    )}
                    <div className="flex-1" />
                    {openTabs.tabIds.length > 1 && editingSessionId !== tab.id && (
                      <button
                        onClick={e => { e.stopPropagation(); handleCloseSession(tab.id) }}
                        // No hover on touch — keep the close affordance always
                        // visible on mobile's full-width rows.
                        className={`p-0.5 hover:themed-bg-tertiary rounded transition-opacity shrink-0 ${mobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                        title="Close tab"
                      >
                        <X size={mobile ? 13 : 10} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="flex items-center shrink-0 sticky top-0 h-[38px]">
              {/* Mobile: display-options rides down here, on the same line as
                  New chat + Session history (row 1 is gone on mobile). */}
              {mobile && <DisplayOptionsMenu options={displayOptions} onToggle={toggleOption} />}
              {(sessionOverview.wrapped || sessionOverview.open) && (
                <button
                  onClick={() => sessionOverview.toggle()}
                  className={`px-2 h-full flex items-center gap-1 transition-colors ${sessionOverview.open ? 'themed-text' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'}`}
                  title={sessionOverview.open ? 'Collapse tabs' : 'Show all tabs'}
                  aria-expanded={sessionOverview.open}
                >
                  <Copy size={13} />
                </button>
              )}
              <button
                onClick={() => { handleNewSession(); sessionOverview.setOpen(false) }}
                className="p-2 themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
                title="New chat"
              >
                <Plus size={14} />
              </button>
              <button
                onClick={() => { setHistoryOpen(true); sessionOverview.setOpen(false) }}
                className="p-2 themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
                title="Session history"
              >
                <History size={14} />
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Mobile: the full "recent chats" list — frosted fog + full-width
          rows, opened by the centered pill / ⧉ count. Same pattern as the
          editor tab list. A child of .ws-card, covering everything below the
          header (top = measured header height); the header (z-40) stays above
          so the pill remains tappable to dismiss. */}
      {mobile && sessionListOpen && (
        <>
          <div
            className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0 z-20"
            onClick={() => setSessionListOpen(false)}
            aria-hidden
          />
          <div
            className="mobile-sheet-in absolute inset-x-0 bottom-0 z-30 flex flex-col gap-1.5 overflow-y-auto px-4 pt-3 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{ top: mobileHeaderH }}
          >
            {displayTabs.map((tab, idx) => {
              const isDraft = tab.id.startsWith(DRAFT_PREFIX)
              const isActive = tab.id === VIRTUAL_ID || openTabs.activeId === tab.id
              const running = (isActive && isProcessing) || busyIds.has(tab.id)
              const done = !running && !isActive && doneIds.has(tab.id)
              return (
                <div
                  key={tab.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (tab.id !== VIRTUAL_ID) { openTabs.setActiveId(tab.id); clearDone(tab.id) } setSessionListOpen(false) }}
                  className={`flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 ${
                    isActive
                      ? 'themed-border themed-bg-tertiary themed-text'
                      : 'themed-border-secondary themed-bg-secondary themed-text-secondary active:themed-bg-tertiary'
                  }`}
                >
                  <span className="inline-flex w-4 shrink-0 items-center justify-center">
                    {running ? (
                      <span className="chat-run-wave" title="Agent running" aria-hidden>
                        <span /><span /><span />
                      </span>
                    ) : done ? (
                      <span className="chat-done-dot" title="Finished while you were away" />
                    ) : (
                      <MessageCircle size={16} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm">{isDraft ? 'New chat' : (tab.title || `Chat ${idx + 1}`)}</span>
                  {openTabs.tabIds.length > 1 && (
                    <button
                      onClick={e => { e.stopPropagation(); handleCloseSession(tab.id) }}
                      aria-label="Close chat"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md themed-text-muted active:themed-bg-tertiary"
                    >
                      <X size={17} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Messages */}
      <MessageList
        /* The element the workspace is currently sharing with the chat — the
           same selection the composer shows as a chip. It is what lets the icon
           card offer WHERE to place the icon; with nothing selected it is null
           and the card says where the icon will go instead. */
        selectedElement={!contextMuted && editorSelection?.kind === 'element' ? (editorSelection.elementPath || editorSelection.elementSelector || null) : null}
        /* The TAG, so the card can withhold the two "inside" placements for an
           element that cannot hold children. The editor gates them on parse5's
           `innerEnd`; the chat has no loc, so the tag is what it can ask. */
        selectedElementTag={!contextMuted && editorSelection?.kind === 'element' ? (editorSelection.elementTag ?? null) : null}
        workspaceFolderId={workspaceFolderId ?? null}
        summarizing={summarizing || compacting}
        sessionId={openTabs.activeId}
        messages={displayMessages}
        streamingText={streamingText}
        streamingReasoning={streamingReasoning}
        pendingPermission={devMockPermission ?? (pendingPermissions[0] ?? null)}
        onPermissionReply={(permissionId, action, always, feedback) => {
          // Swallow ONLY the mock's own reply — a blanket `if (devMock)`
          // swallowed REAL replies whenever any Render-menu mock was
          // active (real question answered → nothing sent → stuck turn).
          if (devMockPermission && permissionId === devMockPermission.id) { setDevMock(null); return }
          replyPermission(permissionId, action, always, feedback)
        }}
        pendingQuestions={devMockQuestion ? [devMockQuestion, ...pendingQuestions] : pendingQuestions}
        expiredQuestionNotice={expiredQuestionNotice}
        // RETURNS the promise. A card that renders a terminal "sent" state must
        // be able to find out the POST failed — otherwise an offline reply, a
        // 5xx or an expired question leaves it claiming success while the turn
        // stays blocked, with the rejection unhandled.
        onQuestionReply={(questionId, answers, entryId, envMapping, result, credentialSaved, credential) => {
          if (devMockQuestion && questionId === devMockQuestion.id) { setDevMock(null); return Promise.resolve() }
          return replyQuestion(questionId, answers, entryId, envMapping, result, credentialSaved, credential)
        }}
        onCredentialResolved={(envMapping, entryId, callId, fieldMap, opts) => {
          // Only a mock CREDENTIAL scenario swallows this (see above).
          if (devMockCred) { setDevMock(null); return false }
          // THE card's own row, by its tool call id (round 11 — the one way to find it). RETURNED (I198 Codex
          // round): the card awaits the binding before it replies, so a follow-up never races the row.
          if (!callId) return false
          return sessionCreds.resolveCard(callId, envMapping, entryId, fieldMap, opts)
        }}
        onQuestionDismiss={(questionId) => {
          if (devMockQuestion && questionId === devMockQuestion.id) { setDevMock(null); return }
          rejectQuestion(questionId)
        }}
        activeToolCalls={activeToolCalls}
        sessionCredentials={displayCredentials}
        isProcessing={isProcessing}
        retry={retry}
        reconnecting={reconnecting}
        turnActivity={turnActivity}
        showReasoning={displayOptions.showReasoning}
        showToolDetails={displayOptions.showToolDetails}
        showModelInfo={displayOptions.showModelInfo}
        showCost={showMessageCost}
        showTimestamps={displayOptions.showTimestamps}
        wrapLines={displayOptions.wrapDiffLines}
        onMessageAction={handleMessageAction}
        workspaceId={workspaceId}
        editingMessageId={editingMessageId}
        onEditStart={isProcessing ? undefined : (id) => {
          // Hydrate the attachment area with the edited message's existing
          // file parts so they survive the re-send (unless user removes
          // them). Without this, editing a message silently drops every
          // attachment it originally had.
          const msg = messages.find(m => m.id === id)
          if (msg) {
            const hydrated: Attachment[] = msg.parts
              .filter((p): p is Extract<typeof p, { type: 'file' }> => p.type === 'file')
              .map(fp => ({
                id: `edit_${fp.id}`,
                filename: fp.filename,
                mime: fp.mime,
                url: fp.url,
                path: fp.path,
                source: (fp.source as Attachment['source']) || 'attach',
                status: 'ready' as const,
                // #639 Task 3.8: carry the handle through so a KEPT
                // session-private binary survives the resend — without it,
                // a re-sent attachment arrives handle-less and the server
                // has no bytes to resolve (its `url` is empty for a
                // handle-only binary part).
                handle: fp.handle,
                // #662: carry the drive fileId too, or editing+resending a
                // moved attachment silently severs its drive link (no tick,
                // and it drops out of the fileId-keyed delete-clear). The
                // outgoing send mapper reads `a.fileId`.
                fileId: fp.fileId,
                // #662: and the stashed inline bytes, so a resent moved
                // attachment can still revert to its inline preview.
                previewUrl: fp.previewUrl,
              }))
            // #1292: the chips this replaces are dropped unsent — cancel their
            // uploads and delete what already landed (composerAttachments.ts).
            attachmentControls.discard(attachments.map(a => a.id))
            setAttachments(hydrated)
          }
          setEditingMessageId(id)
        }}
        onEditCancel={() => {
          // Drop the hydrated attachments so they don't bleed into the
          // main chat input when the user goes back to composing fresh —
          // and, #1292, any chip added during the edit: dropped unsent.
          attachmentControls.discard(attachments.map(a => a.id))
          setAttachments([])
          setEditingMessageId(null)
        }}
        onEditSend={handleEditSend}
        onOpenFile={onOpenFile ? handleTranscriptOpenFile : undefined}
        onMoved={moveAttachmentPart}
        activePersona={personaId}
        emptyState={<ProjectWelcome linkedFolders={scopeLinks} onStart={startFromWelcome} entityKind={entityKind} projectCanWrite={canWriteProp} />}
        renderEditControls={editingMessageId ? (editText, onCancel, onSend) => (
          <>
            <AttachmentArea
              attachments={attachments}
              onRemove={handleRemoveAttachment}
              onOpen={onOpenFile}
              disabled={false}
              sessionId={openTabs.activeId}
              workspaceId={workspaceId}
              onMoved={handleComposerAttachmentMoved}
            />
            {/* Same wrap rule as the composer row below: the left group never
                shrinks past its min-content, so the actions drop to their own
                right-aligned line only when the two can't share one. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
              <div className="flex items-center gap-2 flex-1 min-w-min">
                <ModelSelector
                  selectedModel={selectedModel}
                  onSelect={handleModelSelect}
                  disabled={false}
                />
                {activeTab && !contextMuted && (
                  <EditorContextChip activeTab={activeTab} selection={editorSelection} onDismiss={onMuteContext} />
                )}
              </div>
              <EditMessageButtons
                onAttach={() => fileInputRef.current?.click()}
                onSend={onSend}
                onCancel={onCancel}
                sendDisabled={!editText.trim()}
              />
            </div>
          </>
        ) : undefined}
      />

      {/* Manual billing's write freeze, said on the surface where most turns
          actually happen. This notice shipped on HeroChat alone, which is only
          a session's FIRST message — so a customer who kept the conversation
          going lost the explanation at exactly the point they were most likely
          to ask the agent to create a file. Same component, same copy, so the
          two cannot drift.

          Above the error row on purpose: it explains a refusal BEFORE it
          happens, where the row below reports one that already has. */}
      {uploadsPaused && <UploadsPausedNotice className="mx-3 mb-2" />}

      {/* Error display */}
      {error && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-start justify-between gap-2">
          <span className="flex-1 min-w-0 break-words whitespace-pre-wrap">
            {typeof error === 'string' ? error : String((error as any)?.message ?? error)}
          </span>
          <div className="flex gap-1.5 shrink-0">
            {/* The fix, when the reader is someone who can apply it. Rendered
                FIRST so it reads before Abort/Dismiss — the message just told
                them what to do and this is it. Both conditions matter: the
                gate decides whether this reader can act, the host decides
                whether it can offer the destination at all. */}
            {errorAction && onOpenBilling && (
              <button
                onClick={() => { onOpenBilling(); dismissError() }}
                className="px-2 py-0.5 rounded bg-red-500/30 hover:bg-red-500/40 text-red-200 font-medium transition-colors"
              >
                {errorAction.label}
              </button>
            )}
            {(
              <button
                onClick={() => { cancel(); dismissError() }}
                className="px-2 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
              >
                Abort
              </button>
            )}
            <button
              onClick={() => dismissError()}
              className="px-2 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Todos */}
      {todos.length > 0 && <TodoPanel todos={todos} />}

      {/* Input */}
      <div className="p-3 border-t themed-border">
        <div
          className="relative flex flex-col themed-bg-tertiary rounded-lg px-3 py-2"
        >
          <FileAutocomplete
            visible={autocomplete.visible}
            items={autocompleteItems}
            selectedIndex={autocomplete.selectedIndex}
            loading={wsFiles.loading}
            onSelect={handleFileSelect}
            onDismiss={() => setAutocomplete(prev => ({ ...prev, visible: false }))}
          />
          {COMMAND_SURFACE_ENABLED && (
            <SlashCommandMenu
              visible={slash.visible}
              commands={slashFiltered}
              selectedIndex={slash.selectedIndex}
              onSelect={runSlashCommand}
              onDismiss={() => setSlash(prev => ({ ...prev, visible: false }))}
            />
          )}
          {COMMAND_SURFACE_ENABLED && paletteOpen && (
            <CommandPalette commands={slashCommands} onClose={() => setPaletteOpen(false)} />
          )}
          {timelineOpen && (
            <TimelineDialog
              messages={messages}
              onJump={(id) => document.getElementById(`agent-msg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              onBranch={(id) => handleMessageAction('branch', id)}
              onClose={() => setTimelineOpen(false)}
            />
          )}
          {/* Pending credential form — inline in chat panel, not blocking editor */}
          {pendingCredToFill && (
            <div className="mx-1 mb-2">
              <CredentialPrompt
                request={{ id: pendingCredToFill.requestId, sessionID: '', questions: [] }}
                file={pendingCredToFill.file}
                label={pendingCredToFill.label}
                envMapping={pendingCredToFill.envMapping}
                isEdit={pendingCredToFill.isEdit}
                initialValues={pendingCredToFill.currentValues}
                initialVersion={pendingCredToFill.currentVersion}
                initialEntryId={pendingCredToFill.entryId}
                provider={pendingCredToFill.provider}
                fields={sessionDialogFields(pendingCredToFill.envMapping)}
                onReply={() => setPendingCredToFill(null)}
                onDismiss={() => setPendingCredToFill(null)}
                onDelete={pendingCredToFill.isEdit ? () => {
                  sessionCreds.remove(pendingCredToFill.requestId)
                  setPendingCredToFill(null)
                } : undefined}
                onCredentialResolved={async (entryId, _saved, credential) => {
                  // entryId is required — the form always saves to the
                  // keybook now. The per-turn resolver looks values up
                  // server-side, so we don't pass any plaintext through.
                  //
                  // CRITICAL: await bind() before sendMessage.
                  // bind() POSTs to /sessions/:id/credentials creating
                  // the session_credential row that the message route's
                  // resolver reads to build credentialEnv. If we fired
                  // sendMessage in parallel, the message route could
                  // run BEFORE the row exists → empty env → bash gets
                  // an unset $TOKEN → "could not read Username" from
                  // git, which is exactly the failure the user kept
                  // seeing. Awaiting closes the race entirely.
                  if (entryId) {
                    await sessionCreds.bind(pendingCredToFill.requestId, entryId, undefined, credential?.fieldMap)   // #1441: the card's exact field map
                  } else {
                    sessionCreds.remove(pendingCredToFill.requestId, 'user')
                  }

                  if (pendingCredToFill.isEdit) {
                    // Edit mode just updates session state — no message sent.
                    setPendingCredToFill(null)
                  } else {
                    // New submission — send a user message announcing the
                    // credential. The server will resolve env vars from
                    // the bound entryId; we just attach a system context
                    // so the AI knows the env vars are available.
                    const fileName = pendingCredToFill.file?.split('/').pop() || pendingCredToFill.label || 'Credentials'
                    const envVars = Object.entries(pendingCredToFill.envMapping).map(([, v]) => `$${v}`).join(', ')
                    sendMessage(
                      `${fileName} credentials ready`,
                      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                      `Credentials submitted by user. Available environment variables: ${envVars}. Use them directly in bash — do not call cred_to_env again.`,
                    )
                    setPendingCredToFill(null)
                  }
                }}
              />
            </div>
          )}
          {/* #758: the chat-storage warning, directly above the composer and
              staying put. The moment it matters is the one just before the
              member attaches something, which is exactly when a toast would
              already be gone. */}
          <ChatQuotaBanner
            usedBytes={chatQuota?.totalBytes ?? 0}
            limitBytes={chatQuota?.limitBytes ?? null}
            onManage={onManageChatStorage}
          />
          {/* Read-only because this conversation belongs to a different wallet
              (0180). Stated in words with the way out beside it: a disabled
              composer with no explanation reads as a bug, and the reason text
              differs by direction — "this is a support conversation" and "you
              are acting as platform support" are for different readers. */}
          {walletLocked && (
            <div className="flex items-start gap-2 mb-1.5 px-2 py-1.5 rounded bg-sky-500/10 border border-sky-500/20">
              <span className="text-[11px] themed-text-muted flex-1 leading-snug">
                {activeSessionWallet?.reason?.message
                  ?? 'This conversation belongs to a different wallet and is read-only here.'}
              </span>
              <button
                type="button"
                onClick={forkForWallet}
                className="text-[11px] px-2 py-0.5 rounded themed-btn-secondary shrink-0"
              >
                Fork to continue
              </button>
            </div>
          )}
          {/* Attachment chips — hidden during edit (shown inline at edited message) */}
          {!editingMessageId && (
            <AttachmentArea
              attachments={attachments}
              onRemove={handleRemoveAttachment}
              onOpen={onOpenFile}
              disabled={false}
              sessionId={openTabs.activeId}
              workspaceId={workspaceId}
              onMoved={handleComposerAttachmentMoved}
            />
          )}
          <div className="relative w-full">
            <textarea
              ref={inputRef}
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              // One source: a hint if there is one, else blank for the ghost,
              // else the resting prompt. Cannot disagree with the overlay.
              placeholder={composerHint ?? (ghostVisible ? '' : 'Ask about your data...')}
              disabled={!!editingMessageId || walletLocked}
              rows={1}
              className="w-full bg-transparent text-sm themed-text outline-none placeholder:themed-text-muted resize-none max-h-[120px] min-h-[20px]"
              style={{ height: 'auto' }}
              onInput={e => {
                const target = e.target as HTMLTextAreaElement
                target.style.height = 'auto'
                target.style.height = `${Math.min(target.scrollHeight, 120)}px`
              }}
            />
            {ghostVisible && (
              <div className="absolute inset-0 pointer-events-none flex items-center text-sm themed-text-muted overflow-hidden whitespace-nowrap">
                <span className="inline-flex items-center gap-1 opacity-50">
                  <kbd className="text-[10px] border themed-border rounded-md px-1.5 py-0.5 leading-none shadow-[0_1px_0_0_rgba(255,255,255,0.1)] bg-white/5">Tab ⇥</kbd>
                  <span className="truncate">{ghostText}</span>
                </span>
              </div>
            )}
          </div>
          {/* The model picker + context chip never shrink below their
              min-content (so the chip keeps its X); when they and the actions
              can't share a line — e.g. at the chat panel's 250px minimum — the
              actions drop to a right-aligned line of their own instead of the
              Send button being pushed out of the composer. */}
          <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 ${editingMessageId ? 'hidden' : ''}`}>
            <div className="flex items-center gap-2 flex-1 min-w-min">
              <ModelSelector
                selectedModel={selectedModel}
                onSelect={handleModelSelect}
                variant={reasoningVariant}
                onVariantChange={selectReasoningVariant}
                disabled={isProcessing}
              />
              <AutoModeButton {...autoMode.chip} onOpen={autoMode.isDraft ? undefined : autoMode.isListed ? refreshSessions : autoMode.reread} />
              {activeTab && !contextMuted && (
                <EditorContextChip activeTab={activeTab} selection={editorSelection} onDismiss={onMuteContext} />
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {/* Dev-only: opt-in via Display options → Developer → "Render & font
                  tools". Both menus also return null in prod. */}
              {displayOptions.showDevTools && (
                <>
                  <DevToolRenderMenu active={devMock} onSelect={setDevMock} />
                  <DevTypographyMenu value={devChatType} onChange={setDevChatType} />
                </>
              )}
              {/* Prompt stash — park/restore unsent drafts (mirrors OpenCode's
                  prompt/stash: 50 entries, newest-first). Hidden from the UI for
                  now (flag) — the wiring is kept intact behind PROMPT_STASH_ENABLED. */}
              {PROMPT_STASH_ENABLED && !isProcessing && (
                <StashMenu
                  entries={stash.entries}
                  currentDraft={message}
                  onStash={() => { stash.push(message); setMessage('') }}
                  onRestore={(entry: StashEntry) => { setMessage(entry.text); stash.remove(entry.id) }}
                  onRemove={stash.remove}
                />
              )}
              {/* The composer's paperclip, which also carries the
                  view-only state — see `ComposerAttachButton`. */}
              {!isProcessing && (
                <ComposerAttachButton canWrite={canWrite} storageFull={ownStorageFull ? 'member' : null} onPick={() => fileInputRef.current?.click()} />
              )}
              {/* Session keybook button — hidden when the deployment has the
                  keybook off AND nothing is bound to this session (0179). */}
              {!isProcessing && (keybookAvailable || displayCredentials.length > 0) && (
                <div className="relative">
                  <button
                    ref={keybookBtnRef}
                    onClick={() => {
                      if (pendingCredToFill) {
                        setPendingCredToFill(null)
                      } else {
                        setShowSessionKeybook(v => !v)
                      }
                    }}
                    className={`${displayCredentials.length > 0 ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-600 hover:text-zinc-400'} transition-colors shrink-0 p-0.5 relative`}
                    title="Session credentials"
                  >
                    <BookKey size={14} />
                    {hasOpenablePending(displayCredentials) && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow-400" />
                    )}
                  </button>
                  {showSessionKeybook && (
                    <SessionKeybookPopover
                      credentials={displayCredentials}
                      onClose={() => setShowSessionKeybook(false)}
                      anchorRef={keybookBtnRef}
                      onPendingClick={async (cred) => {
                        setShowSessionKeybook(false)
                        // For edit mode (active cred with a backing
                        // keybook entry) we need to pre-fill the form
                        // with current values. Fetch them on demand
                        // since values are no longer cached in browser
                        // memory.
                        let currentValues: Record<string, string> | undefined
                        let currentVersion: number | undefined
                        if (cred.status === 'active' && cred.entryId) {
                          try {
                            const { keybookApi } = await import('@papan/keybook/client')
                            const detail = await keybookApi.getEntry(cred.entryId)
                            const data = (detail.entry.data ?? { fields: {} }) as { fields: Record<string, string> }
                            currentValues = data.fields
                            // #1324 I2: Update is sent against the version THESE values were read at
                            const v = (detail.entry as { version?: unknown }).version
                            currentVersion = typeof v === 'number' ? v : undefined
                          } catch {
                            // Entry deleted or revoked — open form blank.
                          }
                        }
                        setPendingCredToFill({ file: cred.file, label: cred.label, envMapping: sessionDialogEnvMapping(cred), requestId: cred.requestId, isEdit: cred.status === 'active', currentValues, currentVersion, entryId: cred.entryId, provider: cred.provider })
                      }}
                      onAddClick={() => {
                        setShowSessionKeybook(false)
                        setShowAddCredDialog(true)
                      }}
                    />
                  )}
                  {showAddCredDialog && (
                    <AddSessionCredentialDialog
                      existingEntryIds={new Set(sessionCreds.credentials.map(c => c.entryId).filter((id): id is string => !!id))}
                      onClose={() => setShowAddCredDialog(false)}
                      onAdd={(resolved) => {
                        // Values from `resolved` are unused here — the
                        // server-resolved per-turn pipeline pulls them
                        // from the keybook entry itself.
                        // the hook tells the model once the row is saved (a draft: once flushDraft lands it)
                        sessionCreds.addFromKeybook({
                          entryId: resolved.entryId,
                          entryLabel: resolved.entryLabel,
                          file: resolved.file,
                          envMapping: resolved.envMapping,
                        })
                      }}
                    />
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileInputChange}
              />

              {isProcessing ? (
                <button
                  onClick={cancel}
                  className="text-red-400 hover:text-red-300 transition-colors shrink-0 p-0.5"
                  title="Cancel"
                >
                  <StopCircle size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={(!message.trim() && attachments.length === 0) || hasUploading || walletLocked}
                  className="themed-text-muted hover:themed-text transition-colors disabled:opacity-30 shrink-0 p-0.5"
                  title="Send (Enter)"
                >
                  <Send size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center mt-1 px-1 gap-1.5">
          <ContextIndicator
            estimatedTokens={estimatedTokens}
            contextLimit={contextLimit}
            compacted={compacted}
            summarizing={summarizing || compacting}
            onSummarize={async (focus?: string) => {
              const t = describeSummarize(await summarize(focus))
              toast[t.kind](t.text)
            }}
          />
          <p className="text-[10px] themed-text-muted flex-1 text-center">
            Enter to send, Shift+Enter for new line, @ to mention files
          </p>
          {sessionCost > 0 && (
            <span className="text-[10px] themed-text-muted shrink-0" title={sessionCostTitle} data-testid="session-cost">
              {formatChatCost(sessionCost, totalsCurrency)}
            </span>
          )}
        </div>
      </div>

      {historyOpen && (
        <SessionListDialog
          workspaceId={workspaceId}
          openTabIds={openTabs.tabIds}
          onOpen={(id) => {
            openTabs.addTab(id)
            openTabs.setActiveId(id)
            setHistoryOpen(false)
          }}
          onClose={() => { setHistoryOpen(false); refreshSessions() }}
          onDelete={(id) => { openTabs.removeTab(id); refreshSessions() }}
        />
      )}

      {restoreTarget && openTabs.activeId && (
        <RestoreVersionsModal
          sessionId={openTabs.activeId}
          messageId={restoreTarget.messageId}
          workspaceId={workspaceId}
          onClose={() => {
            pendingEditRef.current = null
            setRestoreTarget(null)
          }}
          onRestored={pendingEditRef.current
            ? handleRestoreAndSendComplete
            : (restored) => {
                toast.success(describeRestored(restored))
              }
          }
          andSend={!!pendingEditRef.current}
          mode={restoreTarget.mode}
        />
      )}

      {editSendDialog && (
        <EditSendDialog
          onChoice={handleEditSendChoice}
          onCancel={() => setEditSendDialog(null)}
        />
      )}
    </div>
  )
}
